import { useEffect, useState } from 'react'
import {
  Box, Text, Button, Table, Badge, Group, Modal, Stack,
  Textarea, Select, ActionIcon, Tooltip, TextInput,
  Center, Loader, SimpleGrid, Divider, Tabs,
  NumberInput, Accordion, Progress, Checkbox, ScrollArea, FileButton, Paper, Card, Menu, Image, Drawer, Indicator,
} from '@mantine/core'
import SmartDateInput from '../components/SmartDateInput'
import LicenseQuickView from '../components/assets/LicenseQuickView'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { useTranslation } from 'react-i18next'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { useAuth } from '../hooks/useAuth'
import {
  IconPlus, IconKey, IconAlertTriangle, IconCheck,
  IconEdit, IconBan, IconUserPlus, IconRefresh,
  IconHistory, IconLink, IconX, IconFileText, IconPaperclip, IconDownload, IconTrash, IconCoin, IconInfoCircle, IconDotsVertical, IconUpload, IconPhoto, IconFilter,
} from '@tabler/icons-react'
import HistoryTimelineModal from '../components/HistoryTimelineModal'
import {
  getLicenses, getLicenseSummary, createLicense, updateLicense, uploadLicenseLogo, deleteLicenseLogo,
  deactivateLicense, reactivateLicense, assignLicense, unassignLicense, renewLicense, downloadLicensePdf,
  getVendors, createVendor, getAssets, getAllAssetsAdmin, getRecipients, getDepartments, bulkDeleteLicenses, deleteLicense,
  uploadLicenseAttachment, deleteLicenseAttachment,
  uploadRenewalAttachment, deleteRenewalAttachment, updateRenewal,
  getContracts, getContractSummary, createContract, updateContract, deleteContract,
  addContractPayment, deleteContractPayment,
  getLicenseHistory,
  uploadContractAttachment, deleteContractAttachment,
  API_BASE_URL,
} from '../api/client'


// ── Force download via backend proxy (avoids CORS on Cloudinary) ─────────────
const API_BASE = API_BASE_URL
const downloadFile = async (url: string, filename: string) => {
  try {
    const proxyUrl = `${API_BASE}/licenses/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`
    const res = await fetch(proxyUrl, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` }
    })
    if (!res.ok) throw new Error('Download failed')
    const blob = await res.blob()
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = filename
    a.click()
    URL.revokeObjectURL(a.href)
  } catch {
    window.open(url, '_blank')
  }
}

const TYPE_COLOR: Record<string, string> = {
  SUBSCRIPTION: 'blue', PERPETUAL: 'violet', MA: 'orange', OEM: 'teal',
  LEASE: 'cyan', SERVICE: 'indigo', HARDWARE: 'grape', OTHER: 'gray',
}

const TYPE_ICON: Record<string, string> = {
  SUBSCRIPTION: '🔄', PERPETUAL: '♾️', MA: '🛠', OEM: '💻',
  LEASE: '📦', SERVICE: '⚙️', HARDWARE: '🖥', OTHER: '📄',
}
const STATUS_COLOR: Record<string, string> = {
  ACTIVE: 'teal', EXPIRING: 'orange', EXPIRED: 'red', INACTIVE: 'gray',
}

const EMPTY_FORM = {
  productName: '', vendor: '', version: '', type: 'SUBSCRIPTION',
  licenseKey: '', orderNumber: '', totalSeats: 1,
  purchaseDate: '', startDate: '', expiryDate: '',
  cost: 0, currency: 'THB', notes: '', managedBy: '', notifyEmail: '',
  reminderDays: [60, 30, 7],
  billingCycle: 'ANNUAL', contractYears: 1, isOnward: false,
  pricingModel: 'FIXED' as 'FIXED' | 'SCHEDULE',
  paymentSchedule: [] as { description: string; dueDate: string; amount: number; frequency: string; _key?: number }[],
}

/**
 * แปลงค่าใช้จ่ายให้เป็น "ค่าใช้จ่ายรายปี" ตาม billingCycle ของ license นั้นๆ
 * - MONTHLY   → cost × 12
 * - QUARTERLY → cost × 4
 * - ANNUAL    → cost ตรงๆ
 * - MULTI_YEAR → cost ÷ contractYears (ถ้าไม่ระบุจำนวนปี คำนวณไม่ได้ คืน null)
 * - ONE_TIME  → ไม่ใช่ค่าใช้จ่ายที่เกิดซ้ำรายปี คืน null (ไม่ใช่ 0 — 0 จะดูเหมือนฟรี)
 * - pricingModel = 'SCHEDULE' → คำนวณจาก payment schedule ที่กำหนดวันเองไม่ตรงเป็นรอบปีเสมอไป
 *   ไม่ใช่ตัวเลขเดียวที่แปลงตรงไปตรงมาได้ คืน null (ให้ไปดูรายละเอียดใน payment schedule แทน)
 */
function getAnnualCost(l: any): number | null {
  if (l.pricingModel === 'SCHEDULE') return null
  if (l.cost == null || l.cost === '') return null
  const cost = Number(l.cost)
  switch (l.billingCycle) {
    case 'MONTHLY':   return cost * 12
    case 'QUARTERLY': return cost * 4
    case 'MULTI_YEAR': return l.contractYears ? cost / l.contractYears : null
    case 'ONE_TIME':  return null
    case 'ANNUAL':
    default:          return cost
  }
}

export default function LicensePage() {
  const { isMobile, isTablet, isSmall } = useBreakpoint()
  const { t, i18n } = useTranslation()
  const { roles, departments: userDepts } = useAuth()
  const isAdminLevel = roles.includes('ADMIN') || roles.includes('SUPER_ADMIN') || roles.includes('ASSET_MANAGER')
  // USER/AUDITOR: มี dept = scoped, ไม่มี dept = เห็นทุกแผนก
  const scopedDepts = !isAdminLevel ? (userDepts ?? []) : []
  const isScopedToDepts = scopedDepts.length > 0
  // dept เริ่มต้นสำหรับ form — ถ้า scoped ใช้อันแรก, ถ้าไม่ scoped = ว่าง (เลือกเองได้)
  const defaultDept = isScopedToDepts ? scopedDepts[0] : ''

  // ── History ──────────────────────────────────────────────────────────────
  const [historyOpened, { open: openHistory, close: closeHistory }] = useDisclosure(false)
  const [historyTarget, setHistoryTarget] = useState<any>(null)
  const [historyData, setHistoryData] = useState<any[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const handleOpenHistory = async (l: any) => {
    setHistoryTarget(l)
    setHistoryData([])
    openHistory()
    setLoadingHistory(true)
    try {
      const res = await getLicenseHistory(l.id)
      setHistoryData(res.data)
    } catch (_) {
      notifications.show({ color: 'red', message: t('license.historyLoadFailed', 'Failed to load history') })
    } finally { setLoadingHistory(false) }
  }

  const [licenses, setLicenses]     = useState<any[]>([])
  const [summary, setSummary]       = useState<any>(null)
  const [loading, setLoading]       = useState(true)
  const [search, setSearch]         = useState('')
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [typeFilter, setTypeFilter] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [inactiveFilter, setInactiveFilter] = useState<boolean>(false)
  const [deptFilter, setDeptFilter] = useState<string | null>(null)
  const [deptOptions, setDeptOptions] = useState<{ value: string; label: string }[]>([])

  // ── Filter bottom sheet (มือถือ) — เหมือน pattern เดียวกับ AssetsPage.tsx: ปุ่ม "ตัวกรอง"
  // เดียวเปิดแผ่นเลื่อนขึ้นจากล่าง แทน Type/Department dropdown ที่เรียงอยู่ในแถบเดิม เลือกเสร็จ
  // ค่อยกด "ใช้ตัวกรอง" — ค่าใช้จ่ายรายปี (KPI การ์ดด้านบน + คอลัมน์ในตาราง) ไม่เกี่ยวข้องกับตัวกรอง
  // นี้เลย ไม่แตะต้อง ยังโชว์ปกติเหมือนเดิมทุกที่ ──
  const [filterSheetOpen, setFilterSheetOpen] = useState(false)
  const [draftType, setDraftType] = useState<string | null>(null)
  const [draftDept, setDraftDept] = useState<string | null>(null)
  const openFilterSheet = () => { setDraftType(typeFilter); setDraftDept(deptFilter); setFilterSheetOpen(true) }
  const applyFilterSheet = () => { setTypeFilter(draftType); setDeptFilter(draftDept); setFilterSheetOpen(false) }
  const clearFilterSheet = () => { setDraftType(null); setDraftDept(null) }
  const activeFilterCount = [typeFilter, deptFilter].filter(Boolean).length
  const [detailLicense, setDetailLicense] = useState<any>(null)
  const [formOpened, { open: openForm, close: closeForm }] = useDisclosure(false)
  const [quickViewLicense, setQuickViewLicense] = useState<any | null>(null)
  const [quickViewOpen, setQuickViewOpen] = useState(false)
  const [editing, setEditing]       = useState<any>(null)
  const [form, setForm]             = useState<any>({ ...EMPTY_FORM })
  const [licenseFiles, setLicenseFiles] = useState<File[]>([])
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [licenseFileNames, setLicenseFileNames] = useState<string[]>([])
  const [editRenewal, setEditRenewal] = useState<any>(null)
  const [editRenewalOpened, { open: openEditRenewal, close: closeEditRenewal }] = useDisclosure(false)
  const [editRenewalForm, setEditRenewalForm] = useState({ renewedFrom:'', renewedTo:'', vendor:'', cost:0, orderNumber:'', note:'' })
  const [savingRenewal, setSavingRenewal] = useState(false)
  const [saving, setSaving]         = useState(false)
  const [vendorOptions, setVendorOptions] = useState<{ value: string; label: string }[]>([])
  const [allAssets, setAllAssets]   = useState<any[]>([])
  const [allRecipients, setAllRecipients] = useState<any[]>([])
  // Quick Add Vendor
  const [qaVendorOpen, { open: openQAVendor, close: closeQAVendor }] = useDisclosure(false)
  const [qaVendorName, setQAVendorName] = useState('')
  const [qaVendorSaving, setQAVendorSaving] = useState(false)

  // Assign modal state
  const [assignLicenseItem, setAssignLicenseItem] = useState<any>(null)
  const [assignModalOpen, { open: openAssignModal, close: closeAssignModal }] = useDisclosure(false)
  const [assignTargetType, setAssignTargetType] = useState<string>('USER')
  const [assignTargetIds, setAssignTargetIds] = useState<string[]>([])
  const [assignTargetNames, setAssignTargetNames] = useState<Record<string, string>>({})
  const [assignNote, setAssignNote] = useState('')
  const [assignSearch, setAssignSearch] = useState('')

  // Contract modal state
  const emptyContract = () => ({
    title: '', vendor: '', contractNumber: '', type: 'SERVICE',
    startDate: '', endDate: '', autoRenew: false, currency: 'THB',
    description: '', contactName: '', contactEmail: '', contactPhone: '',
    managedBy: '', notes: '',
  })
  const emptyPaymentRow = () => ({ description: '', dueDate: '', amount: 0, currency: 'THB', frequency: 'YEARLY', notes: '', _key: Date.now() })
  const [contractModalOpen, { open: openContractModal, close: closeContractModal }] = useDisclosure(false)
  const [editingContract, setEditingContract] = useState<any>(null)
  const [contractForm, setContractForm] = useState<any>(emptyContract())
  const [contractPayments, setContractPayments] = useState<any[]>([])
  const [savingContract, setSavingContract] = useState(false)
  const [contracts, setContracts] = useState<any[]>([])
  const [contractSummary, setContractSummary] = useState<any>(null)

  const CONTRACT_TYPES_OPT = [
    { value: 'MAINTENANCE', label: '🛠 Maintenance Agreement' },
    { value: 'LEASE',       label: '📦 Lease / Rental' },
    { value: 'SERVICE',     label: '⚙️ Service Agreement' },
    { value: 'SOFTWARE',    label: '💿 Software Contract' },
    { value: 'HARDWARE',    label: '🖥 Hardware Support' },
    { value: 'OTHER',       label: '📄 Other' },
  ]
  const PAYMENT_FREQ = [
    { value: 'ONE_TIME',  label: 'One-time' },
    { value: 'MONTHLY',   label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'YEARLY',    label: 'Yearly' },
  ]
  const fmtMoney = (n: any, cur = 'THB') =>
    `${cur} ${Number(n || 0).toLocaleString()}`
  const contractTotal = contractPayments.reduce((s, p) => s + Number(p.amount || 0), 0)

  const handleOpenNewContract = () => {
    setEditingContract(null)
    setContractForm(emptyContract())
    setContractPayments([])
    openContractModal()
  }
  const handleOpenEditContract = (c: any) => {
    setEditingContract(c)
    setContractForm({
      title: c.title ?? '', vendor: c.vendor ?? '', contractNumber: c.contractNumber ?? '',
      type: c.type ?? 'SERVICE', startDate: c.startDate?.slice(0,10) ?? '',
      endDate: c.endDate?.slice(0,10) ?? '', autoRenew: c.autoRenew ?? false,
      currency: c.currency ?? 'THB', description: c.description ?? '',
      contactName: c.contactName ?? '', contactEmail: c.contactEmail ?? '',
      contactPhone: c.contactPhone ?? '', managedBy: c.managedBy ?? '', notes: c.notes ?? '',
    })
    setContractPayments((c.payments ?? []).map((p: any) => ({ ...p, dueDate: p.dueDate?.slice(0,10), _key: p.id })))
    openContractModal()
  }
  const handleSaveContract = async () => {
    if (!contractForm.title.trim()) return notifications.show({ color: 'red', message: 'Contract title is required' })
    setSavingContract(true)
    try {
      const payload = { ...contractForm, payments: contractPayments }
      editingContract
        ? await updateContract(editingContract.id, payload)
        : await createContract(payload)
      notifications.show({ color: 'teal', message: 'Contract saved' })
      closeContractModal()
      loadContracts()
    } catch { notifications.show({ color: 'red', message: 'Failed to save contract' }) }
    finally { setSavingContract(false) }
  }
  const handleDeleteContract = (c: any) => modals.openConfirmModal({
    title: t('license.deleteContractTitle', 'Delete Contract'),
    children: <Text size="sm">{t('license.deleteContractBody', 'Delete {{title}}? This cannot be undone.', { title: c.title })}</Text>,
    labels: { confirm: t('common.delete', 'Delete'), cancel: t('common.cancel', 'Cancel') },
    confirmProps: { color: 'red' },
    onConfirm: async () => { await deleteContract(c.id); loadContracts() },
  })

  const loadContracts = async () => {
    try {
      const [c, s] = await Promise.all([getContracts(), getContractSummary()])
      setContracts(c.data)
      setContractSummary(s.data)
    } catch {}
  }

  const handleQuickAddVendor = async () => {
    if (!qaVendorName.trim()) return
    setQAVendorSaving(true)
    try {
      await createVendor({ name: qaVendorName.trim() })
      const newOpt = { value: qaVendorName.trim(), label: qaVendorName.trim() }
      setVendorOptions(prev => [...prev, newOpt])
      f('vendor')(qaVendorName.trim())
      notifications.show({ color: 'teal', message: `เพิ่ม Vendor "${qaVendorName.trim()}" สำเร็จ` })
      setQAVendorName('')
      closeQAVendor()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
    } finally { setQAVendorSaving(false) }
  }

  const load = async () => {
    setLoading(true)
    try {
      const [lic, sum, vends, assetRes, recipientsRes, depts] = await Promise.all([
        getLicenses(), getLicenseSummary(), getVendors(), getAllAssetsAdmin(), getRecipients(), getDepartments()
      ])
      setLicenses(lic.data)
      setSummary(sum.data)
      setVendorOptions(
        (vends.data as any[]).filter(v => v.isActive).map(v => ({ value: v.name, label: v.name }))
      )
      setAllAssets(assetRes.data ?? [])
      setAllRecipients((recipientsRes.data ?? []).filter((r: any) => r.isActive !== false))
      setDeptOptions((depts.data as any[]).filter(d => d.isActive !== false).map(d => ({ value: d.code, label: d.name ?? d.code })))
    } finally { setLoading(false) }
  }

  useEffect(() => { load(); loadContracts() }, [])

  const filtered = licenses.filter(l => {
    const matchSearch = !search || l.productName.toLowerCase().includes(search.toLowerCase()) || (l.vendor ?? '').toLowerCase().includes(search.toLowerCase())
    const matchType   = !typeFilter   || l.type   === typeFilter
    const matchDept   = !deptFilter   || l.departmentCode === deptFilter
    const matchStatus = !statusFilter || l.status === statusFilter
    // inactiveFilter=true → แสดงแค่ inactive | false (default) → ซ่อน inactive
    const matchActive = inactiveFilter ? l.isActive === false : l.isActive !== false
    return matchSearch && matchType && matchDept && matchStatus && matchActive
  })

  const f = (k: string) => (v: any) => setForm((p: any) => ({ ...p, [k]: v }))

  // ── Open create/edit ──────────────────────────────────────────────────────
  const handleOpenCreate = () => {
    setEditing(null)
    setForm({ ...EMPTY_FORM, departmentCode: defaultDept })
    setLicenseFiles([]); setLicenseFileNames([])
    setLogoFile(null); setLogoPreview(null)
    openForm()
  }
  const handleOpenEdit = (l: any) => {
    setEditing(l)
    setForm({
      productName: l.productName, vendor: l.vendor ?? '', version: l.version ?? '',
      type: l.type, licenseKey: l.licenseKey ?? '', orderNumber: l.orderNumber ?? '',
      totalSeats: l.totalSeats, purchaseDate: l.purchaseDate?.slice(0,10) ?? '',
      startDate: l.startDate?.slice(0,10) ?? '', expiryDate: l.expiryDate?.slice(0,10) ?? '',
      // ── ช่อง Cost ในฟอร์ม Edit นี้แก้ไข "ราคาตั้งต้น (Original)" เท่านั้น ไม่ใช่ราคาปัจจุบัน — ราคา
      // ปัจจุบันเปลี่ยนได้ทาง Renew เท่านั้น (ดู update() ใน licenses.service.ts) — prefill จาก
      // originalCost ก่อนเสมอ ถ้ายังไม่เคยมีเลยค่อย fallback ไปที่ cost (สำหรับ license ที่ยังไม่เคย
      // renew เลย ค่าทั้งสองจะเท่ากันอยู่แล้วโดยธรรมชาติ ไม่ต่างกัน) ──
      cost: l.cost ?? 0, currency: l.currency ?? 'THB', notes: l.notes ?? '',
      managedBy: l.managedBy ?? '', notifyEmail: l.notifyEmail ?? '',
      reminderDays: l.reminderDays ?? [60,30,7],
      departmentCode: l.departmentCode ?? defaultDept,
      billingCycle: l.billingCycle ?? 'ANNUAL',
      contractYears: l.contractYears ?? 1,
      isOnward: l.isOnward ?? false,
      pricingModel: l.pricingModel ?? 'FIXED',
      paymentSchedule: (l.paymentSchedule ?? []).map((p: any) => ({
        ...p,
        dueDate: p.dueDate?.slice(0, 10) ?? '',
        _key: Math.random(),
      })),
    })
    setLicenseFiles([]); setLicenseFileNames([])
    setLogoFile(null); setLogoPreview(l.logoUrl ?? null)
    openForm()
  }

  const handleSave = async () => {
    if (!form.productName.trim()) { notifications.show({ color: 'red', message: t('license.productNameRequired', 'Product name required') }); return }
    setSaving(true)
    try {
      const payload = {
        ...form,
        totalSeats: Number(form.totalSeats),
        cost: form.pricingModel === 'SCHEDULE' ? undefined : (form.cost ? Number(form.cost) : undefined),
        purchaseDate: form.purchaseDate || undefined,
        startDate: form.startDate || undefined,
        expiryDate: form.isOnward ? undefined : (form.expiryDate || undefined),
        billingCycle: form.pricingModel === 'SCHEDULE' ? undefined : (form.billingCycle || 'ANNUAL'),
        contractYears: form.billingCycle === 'MULTI_YEAR' && form.pricingModel !== 'SCHEDULE' ? Number(form.contractYears) : undefined,
        isOnward: form.isOnward ?? false,
        pricingModel: form.pricingModel ?? 'FIXED',
        paymentSchedule: form.pricingModel === 'SCHEDULE' ? (form.paymentSchedule ?? []).map(({ _key, ...p }: any) => p) : undefined,
      }
      let savedId = editing?.id
      if (editing) {
        await updateLicense(editing.id, payload)
      } else {
        const res = await createLicense(payload)
        savedId = res.data.id
      }
      // Upload attachments if any (only on create, or can be added via detail view on edit)
      if (!editing && licenseFiles.length > 0 && savedId) {
        await Promise.all(licenseFiles.map(f => uploadLicenseAttachment(savedId, f)))
      }
      // Upload logo if a new one was picked
      if (logoFile && savedId) {
        try { await uploadLicenseLogo(savedId, logoFile) }
        catch (err: any) {
          console.error('Logo upload failed:', err?.response?.data ?? err)
          const detail = err?.response?.data?.message ?? err?.message ?? ''
          notifications.show({ color: 'orange', message: `${t('licenses.logoUploadFailed', 'Saved, but logo upload failed')}${detail ? `: ${detail}` : ''}` })
        }
      }
      notifications.show({ color: 'teal', message: editing ? t('licenses.licenseUpdatedMsg', 'License updated') : t('licenses.licenseCreatedMsg', 'License created{{fileSuffix}}', { fileSuffix: licenseFiles.length > 0 ? ` — ${licenseFiles.length} file(s) attached` : '' }) })
      closeForm(); load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error', 'Error') })
    } finally { setSaving(false) }
  }

  // ── View detail (assignments, renewals) ───────────────────────────────────
  const handleViewDetail = (l: any) => { setQuickViewLicense(l); setQuickViewOpen(true) }

  // ── Assign ────────────────────────────────────────────────────────────────
  const handleAssign = (l: any) => {
    setAssignLicenseItem(l)
    setAssignTargetType('USER')
    setAssignTargetIds([])
    setAssignTargetNames({})
    setAssignNote('')
    setAssignSearch('')
    openAssignModal()
  }

  const confirmAssign = async () => {
    if (!assignTargetIds.length || !assignLicenseItem) return
    try {
      for (const targetId of assignTargetIds) {
        await assignLicense(assignLicenseItem.id, {
          targetType: assignTargetType,
          targetId: targetId.trim(),
          targetName: assignTargetNames[targetId] || undefined,
          note: assignNote || undefined,
        })
      }
      notifications.show({ color: 'teal', message: `Assigned ${assignTargetIds.length} item(s)` })
      setAssignTargetIds([]); setAssignTargetNames({}); setAssignNote(''); setAssignSearch('')
      closeAssignModal()
      load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
    }
  }

  // ── Renew ─────────────────────────────────────────────────────────────────
  // ── Renew state ───────────────────────────────────────────────────────────
  const [renewTarget, setRenewTarget] = useState<any>(null)
  const [renewOpened, { open: openRenew, close: closeRenew }] = useDisclosure(false)
  const [renewForm, setRenewForm] = useState<{ renewedFrom: string; renewedTo: string; vendor: string; cost: number | null; orderNumber: string; note: string }>({ renewedFrom: '', renewedTo: '', vendor: '', cost: null, orderNumber: '', note: '' })
  const [renewFiles, setRenewFiles] = useState<File[]>([])
  const [renewFileNames, setRenewFileNames] = useState<string[]>([])
  const [renewing, setRenewing] = useState(false)

  const handleRenew = (l: any) => {
    setRenewTarget(l)
    // Auto-suggest next expiry based on billing cycle (skip if Onward)
    const fromDate = l.expiryDate ? new Date(l.expiryDate) : new Date()
    let suggestedTo = ''
    if (!l.isOnward) {
      if (l.billingCycle === 'MONTHLY') {
        const d = new Date(fromDate); d.setMonth(d.getMonth() + 1)
        suggestedTo = d.toISOString().slice(0, 10)
      } else if (l.billingCycle === 'QUARTERLY') {
        const d = new Date(fromDate); d.setMonth(d.getMonth() + 3)
        suggestedTo = d.toISOString().slice(0, 10)
      } else if (l.billingCycle === 'MULTI_YEAR') {
        const d = new Date(fromDate); d.setFullYear(d.getFullYear() + (l.contractYears ?? 1))
        suggestedTo = d.toISOString().slice(0, 10)
      } else {
        const d = new Date(fromDate); d.setFullYear(d.getFullYear() + 1)
        suggestedTo = d.toISOString().slice(0, 10)
      }
    }
    setRenewForm({
      renewedFrom: l.expiryDate?.slice(0,10) ?? new Date().toISOString().slice(0,10),
      // ── บังคับกรอกราคาทุกครั้งแล้ว (ดู RenewLicenseDto) — เลยไม่ prefill ราคาเดิมไว้ให้ (ยกเว้น
      // MULTI_YEAR ที่ราคามักใกล้เคียงเดิม เป็นจุดเริ่มต้นที่สมเหตุสมผล) กันไม่ให้กดผ่านฟอร์มไป
      // เฉยๆ โดยไม่ได้ตั้งใจกรอกราคาจริงของรอบต่ออายุนี้ ──
      renewedTo: suggestedTo, vendor: l.vendor ?? '', cost: l.billingCycle === 'MULTI_YEAR' ? (l.cost ?? null) : null, orderNumber: '', note: '',
    })
    setRenewFiles([]); setRenewFileNames([])
    openRenew()
  }

  const handleRenewSubmit = async () => {
    if (!renewForm.renewedTo) { notifications.show({ color: 'red', message: t('license.expiryRequired', 'New expiry date required') }); return }
    if (renewForm.cost === null || renewForm.cost === undefined) { notifications.show({ color: 'red', message: t('license.renewalCostRequired', 'Renewal cost is required') }); return }
    setRenewing(true)
    try {
      const res = await renewLicense(renewTarget.id, {
        renewedFrom: renewForm.renewedFrom,
        renewedTo: renewForm.renewedTo,
        cost: renewForm.cost,
        orderNumber: renewForm.orderNumber || undefined,
        vendor: renewForm.vendor || undefined,
        note: renewForm.note || undefined,
      })
      // Upload attachments to the new renewal
      if (renewFiles.length > 0) {
        const newRenewal = res.data
        await Promise.all(renewFiles.map(f => uploadRenewalAttachment(newRenewal.id, f)))
      }
      notifications.show({ color: 'teal', message: `License renewed${renewFiles.length > 0 ? ` — ${renewFiles.length} file(s) attached` : ''}` })
      closeRenew(); load()
    } catch { notifications.show({ color: 'red', message: 'Renew failed' }) }
    finally { setRenewing(false) }
  }

  // ── Print PDF Report ─────────────────────────────────────────────────────
  const handlePrintReport = async () => {
    const toPrint = selectedIds.length > 0
      ? licenses.filter(l => selectedIds.includes(l.id))
      : filtered

    if (!toPrint.length) {
      notifications.show({ color: 'orange', message: t('license.noItemsToPrint', 'No items to print') })
      return
    }

    try {
      const res = await downloadLicensePdf(toPrint.map(l => l.id), i18n.language === 'th' ? 'th' : 'en')
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 30_000)
      notifications.show({ color: 'teal', message: t('license.printPreviewOpened', 'Opened print preview for {{count}} item(s)', { count: toPrint.length }) })
    } catch {
      notifications.show({ color: 'red', message: t('license.pdfGenerationFailed', 'Failed to generate PDF') })
    }
  }

  // ── Single Delete (ADMIN only) ────────────────────────────────────────────
  const handleDeleteOne = (l: any) => {
    modals.openConfirmModal({
      title: 'Delete License',
      children: (
        <Text size="sm">
          Delete <b>{l.productName}</b>? This will also remove all assignments, renewals, and attachments. This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteLicense(l.id)
          notifications.show({ color: 'teal', message: `"${l.productName}" deleted` })
          load()
        } catch {
          notifications.show({ color: 'red', message: 'Delete failed' })
        }
      },
    })
  }

  // ── Bulk Delete ───────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    if (!selectedIds.length) return
    modals.openConfirmModal({
      title: 'ลบ License ที่เลือก',
      children: <Text size="sm">{t('licenses.confirmDeleteMulti', 'Confirm delete {{count}} item(s)? Cannot be undone.', { count: selectedIds.length })}</Text>,
      labels: { confirm: 'ลบทั้งหมด', cancel: 'ยกเลิก' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setBulkDeleting(true)
        try {
          await bulkDeleteLicenses(selectedIds)
          notifications.show({ color: 'teal', message: `ลบ ${selectedIds.length} รายการเรียบร้อย` })
          setSelectedIds([])
          load()
        } catch { notifications.show({ color: 'red', message: 'เกิดข้อผิดพลาด' }) }
        finally { setBulkDeleting(false) }
      },
    })
  }

  // ── Expiry bar ────────────────────────────────────────────────────────────
  const ExpiryBar = ({ l }: { l: any }) => {
    if (l.isOnward) return (
      <Box style={{ minWidth: 100 }}>
        <Text size="xs" c="cyan.6" fw={600}>🔄 Onward</Text>
        <Text size="10px" c="dimmed">{t("license.autoRenew","Auto-renew")}</Text>
      </Box>
    )
    if (!l.expiryDate) return <Text size="xs" c="dimmed">{t("license.noExpiry","No expiry")}</Text>
    const days = l.daysUntilExpiry
    if (days === null) return null
    const color = days < 0 ? 'red' : days <= 7 ? 'red' : days <= 30 ? 'orange' : 'teal'
    return (
      <Box style={{ minWidth: 100 }}>
        <Text size="xs" c={color} fw={days <= 30 ? 600 : 400}>
          {days < 0 ? 'EXPIRED' : days === 0 ? 'Today' : `${days}d left`}
        </Text>
        <Text size="10px" c="dimmed">{l.expiryDate.slice(0,10)}</Text>
      </Box>
    )
  }

  return (
    <Box p={isSmall ? 16 : 32}>
      {/* Header */}
      <Group justify="space-between" mb="xl">
        <Box>
          <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: 24, fontWeight: 600, letterSpacing: '-0.3px', color: '#0f172a' }}>
            {t('licenses.pageTitle','Contract & Subscriptions')}
          </Text>
          {isScopedToDepts ? (
            <Group gap={6} align="center" wrap="wrap">
              <Text size="sm" c="dimmed">{t('licenses.showingFrom','แสดงข้อมูลจาก')}</Text>
              {scopedDepts.map(dept => (
                <Badge key={dept} color="teal" variant="light" size="sm">
                  {deptOptions.find(d => d.value === dept)?.label ?? dept}
                </Badge>
              ))}
            </Group>
          ) : (
            <Text size="sm" c="dimmed">{t('licenses.subtitle','Contracts, subscriptions, perpetual and OEM license management')}</Text>
          )}
        </Box>
        <Group gap={8}>
          {isAdminLevel && selectedIds.length > 0 && (
            <Button variant="light" color="red" loading={bulkDeleting}
              leftSection={<IconBan size={15} />} onClick={handleBulkDelete}>
              ลบที่เลือก ({selectedIds.length})
            </Button>
          )}
          <Button variant="light" color="teal"
            leftSection={<IconFileText size={15} />}
            onClick={handlePrintReport}>
            {selectedIds.length > 0 ? t('license.printReportCount', 'Print Report ({{count}})', { count: selectedIds.length }) : t('license.printAll', 'Print All')}
          </Button>
          <Button leftSection={<IconPlus size={15} />} onClick={handleOpenCreate}
            style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
            {t('licenses.addLicenseBtn','Add License / Subscription')}
          </Button>
        </Group>
      </Group>

      {/* Summary */}
      {summary && (
        <SimpleGrid cols={{ base: 2, md: 7 }} mb="md">
          {[
            { label: t('licenses.total', 'Total'),              value: summary.total,                    color: '#0f172a', border: '#cbd5e1', bg: '#f8fafc', icon: '📋', status: 'ALL' },
            { label: t('licenses.active', 'Active'),            value: summary.active,                   color: '#0d9488', border: '#99f6e4', bg: '#f0fdfa', icon: '✅', status: 'ACTIVE' },
            { label: t('licenses.expiring', 'Expiring Soon'),   value: summary.expiring,                 color: '#d97706', border: '#fcd34d', bg: '#fffbeb', icon: '⚠️', status: 'EXPIRING' },
            { label: t('licenses.expired', 'Expired'),          value: summary.expired,                  color: '#dc2626', border: '#fca5a5', bg: '#fff1f2', icon: '❌', status: 'EXPIRED' },
            { label: t('licenses.inactive','Inactive'),                             value: summary.inactive ?? 0,            color: '#64748b', border: '#cbd5e1', bg: '#f8fafc', icon: '🚫', status: 'INACTIVE' },
            { label: t('licenses.annualCost', 'Annual Cost'),   value: `฿${Number(summary.totalAnnualCost).toLocaleString()}`, color: '#6366f1', border: '#c4b5fd', bg: '#f5f3ff', icon: '💰', status: null, tip: t('licenses.annualCostTip', 'Projected run-rate based on the latest known price for each license.') },
            { label: t('licenses.thisYearCost', 'This Year Cost'), value: `฿${Number(summary.totalThisYearCost ?? 0).toLocaleString()}`, color: '#0891b2', border: '#a5f3fc', bg: '#ecfeff', icon: '📅', status: null, tip: t('licenses.thisYearCostTip', "This calendar year's actual rate, based on the renewal period covering today.") },
          ].map(c => {
            const isActive = c.status === 'ALL'
              ? statusFilter === null && inactiveFilter === false
              : c.status === 'INACTIVE'
                ? inactiveFilter === true
                : c.status !== null && statusFilter === c.status && inactiveFilter === false
            return (
              <Tooltip key={c.label} label={(c as any).tip} disabled={!(c as any).tip} multiline w={220} withArrow>
              <Box
                onClick={() => {
                  if (c.status === 'ALL') {
                    setStatusFilter(null); setTypeFilter(null); setInactiveFilter(false)
                  } else if (c.status === 'INACTIVE') {
                    setInactiveFilter(true); setStatusFilter(null); setTypeFilter(null)
                  } else if (c.status !== null) {
                    const newStatus = isActive ? null : c.status
                    setStatusFilter(newStatus); setInactiveFilter(false)
                    if (newStatus) setTypeFilter(null)
                  }
                }}
                style={{
                  background: c.bg, borderRadius: 12,
                  border: `1px solid ${isActive ? c.color : c.border}`,
                  borderLeft: `4px solid ${isActive ? c.color : c.border}`,
                  padding: '14px 16px',
                  cursor: c.status !== null ? 'pointer' : 'default',
                  boxShadow: isActive ? `0 0 0 2px ${c.border}` : undefined,
                  transition: 'all 0.15s',
                }}>
                <Group gap={6} mb={4}>
                  <Text style={{ fontSize: 14 }}>{c.icon}</Text>
                  <Text style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{c.label}</Text>
                </Group>
                <Text style={{ fontSize: 22, fontWeight: 800, color: c.color, letterSpacing: '-0.5px' }}>{c.value}</Text>
              </Box>
              </Tooltip>
            )
          })}
        </SimpleGrid>
      )}

      {/* ── Type breakdown cards ── */}
      {summary && (
        <Group gap={10} mb="md" wrap="wrap">
          {[
            { type: 'SUBSCRIPTION', icon: '🔄', color: 'blue',   label: t('licenses.typeSubscription','Subscription') },
            { type: 'PERPETUAL',    icon: '♾️',  color: 'violet', label: t('licenses.typePerpetual','Perpetual') },
            { type: 'MA',           icon: '🛠',  color: 'orange', label: t('licenses.typeMA','Maintenance') },
            { type: 'OEM',          icon: '💻',  color: 'teal',   label: t('licenses.typeOEM','OEM') },
            { type: 'LEASE',        icon: '📦',  color: 'cyan',   label: t('licenses.typeLease','Lease/Rental') },
            { type: 'SERVICE',      icon: '⚙️',  color: 'indigo', label: t('licenses.typeService','Service Agr.') },
            { type: 'HARDWARE',     icon: '🖥',  color: 'grape',  label: t('licenses.typeHardware','Hardware Sup.') },
            { type: 'OTHER',        icon: '📄',  color: 'gray',   label: t('licenses.typeOther','Other') },
          ].map(({ type, icon, color, label }) => {
            const count = licenses.filter(l => l.type === type).length
            if (count === 0) return null
            const active = typeFilter === type
            return (
              <Paper key={type} withBorder radius="md" p="sm"
                onClick={() => setTypeFilter(active ? null : type)}
                style={{
                  cursor: 'pointer', minWidth: 110, textAlign: 'center',
                  borderColor: active ? `var(--mantine-color-${color}-5)` : undefined,
                  background: active ? `var(--mantine-color-${color}-0)` : '#fff',
                  transition: 'all 0.15s',
                }}>
                <Text size="lg" mb={2}>{icon}</Text>
                <Text size="xl" fw={700} c={`${color}.6`}>{count}</Text>
                <Text size="xs" c="dimmed" fw={500}>{label}</Text>
              </Paper>
            )
          })}
        </Group>
      )}

      {/* ── Expiring soon alert — 60 วัน (ป้ายเตือนล่วงหน้า) อยู่บนสุด ก่อน 30 วัน (เร่งด่วนกว่า) ── */}
      {licenses.filter(l => !l.isOnward && l.daysUntilExpiry !== null && l.daysUntilExpiry > 30 && l.daysUntilExpiry <= 60).length > 0 && (
        <Box mb="md" p="sm" style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 10 }}>
          <Group gap={8} wrap="wrap">
            <Text size="sm" fw={600} c="blue.7">⏰ {t('license.expiringWithin60', 'Expiring within 60 days:')}</Text>
            {licenses
              .filter(l => !l.isOnward && l.daysUntilExpiry !== null && l.daysUntilExpiry > 30 && l.daysUntilExpiry <= 60)
              .sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry)
              .map(l => (
                <Badge key={l.id} size="sm" color="blue" variant="filled"
                  style={{ cursor: 'pointer' }} onClick={() => { setQuickViewLicense(l); setQuickViewOpen(true) }}>
                  {l.productName} · {l.daysUntilExpiry}d
                </Badge>
              ))}
          </Group>
        </Box>
      )}

      {/* ── Expiring soon alert ── */}
      {licenses.filter(l => !l.isOnward && l.daysUntilExpiry !== null && l.daysUntilExpiry >= 0 && l.daysUntilExpiry <= 30).length > 0 && (
        <Box mb="md" p="sm" style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10 }}>
          <Group gap={8} wrap="wrap">
            <Text size="sm" fw={600} c="orange.7">⚠️ {t('license.expiringWithin30', 'Expiring within 30 days:')}</Text>
            {licenses
              .filter(l => !l.isOnward && l.daysUntilExpiry !== null && l.daysUntilExpiry >= 0 && l.daysUntilExpiry <= 30)
              .sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry)
              .map(l => (
                <Badge key={l.id} size="sm" color={l.daysUntilExpiry <= 7 ? 'red' : 'orange'} variant="filled"
                  style={{ cursor: 'pointer' }} onClick={() => { setQuickViewLicense(l); setQuickViewOpen(true) }}>
                  {l.productName} · {l.daysUntilExpiry}d
                </Badge>
              ))}
          </Group>
        </Box>
      )}

      {/* Table */}
      <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
        <Box style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Text style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{t("license.registry","License Registry")}</Text>
          {isMobile ? (
            <Indicator label={activeFilterCount || undefined} disabled={activeFilterCount === 0} size={16} color="teal">
              <Button variant="default" size="xs" leftSection={<IconFilter size={14} />} onClick={openFilterSheet}>
                {t('assets.filterBtn', 'Filter')}
              </Button>
            </Indicator>
          ) : (<>
          <Select size="xs" placeholder={t("license.allTypes","All types")} clearable value={typeFilter} onChange={setTypeFilter}
            data={[
              { value: 'SUBSCRIPTION', label: '🔄 Subscription' },
              { value: 'PERPETUAL',    label: '♾️ Perpetual' },
              { value: 'MA',           label: '🛠 Maintenance' },
              { value: 'OEM',          label: '💻 OEM' },
              { value: 'LEASE',        label: '📦 Lease/Rental' },
              { value: 'SERVICE',      label: '⚙️ Service Agreement' },
              { value: 'HARDWARE',     label: '🖥 Hardware Support' },
              { value: 'OTHER',        label: '📄 Other' },
            ]} style={{ width: 180 }} />
          {isAdminLevel && (
            <Select size="xs" placeholder={t("license.allDepartments","All departments")} clearable searchable
              value={deptFilter} onChange={setDeptFilter}
              data={deptOptions} style={{ width: 180 }} />
          )}
          </>)}
          <TextInput placeholder={t("license.searchPlaceholder","Search...")} size="xs" value={search}
            onChange={e => setSearch(e.target.value)} style={{ width: isMobile ? '100%' : 200, marginLeft: isMobile ? 0 : 'auto' }}
            styles={{ input: { borderColor: '#e2e8f0' } }} />
        </Box>

        {/* ── มือถือ: แผ่นตัวกรองเลื่อนขึ้นจากล่าง ── */}
        <Drawer opened={filterSheetOpen} onClose={() => setFilterSheetOpen(false)}
          position="bottom" size="60%" radius="lg 0 0 0"
          title={<Text fw={700}>{t('assets.filterBtn', 'Filter')}</Text>}>
          <Stack gap="md" pb={70}>
            <Select
              label={t("license.type", "Type")}
              placeholder={t("license.allTypes","All types")} clearable
              value={draftType} onChange={setDraftType}
              data={[
                { value: 'SUBSCRIPTION', label: '🔄 Subscription' },
                { value: 'PERPETUAL',    label: '♾️ Perpetual' },
                { value: 'MA',           label: '🛠 Maintenance' },
                { value: 'OEM',          label: '💻 OEM' },
                { value: 'LEASE',        label: '📦 Lease/Rental' },
                { value: 'SERVICE',      label: '⚙️ Service Agreement' },
                { value: 'HARDWARE',     label: '🖥 Hardware Support' },
                { value: 'OTHER',        label: '📄 Other' },
              ]} />
            {isAdminLevel && (
              <Select
                label={t("common.department", "Department")}
                placeholder={t("license.allDepartments","All departments")} clearable searchable
                value={draftDept} onChange={setDraftDept}
                data={deptOptions} />
            )}
          </Stack>
          <Box style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: '1px solid #e2e8f0', padding: 16 }}>
            <Group grow>
              <Button variant="default" onClick={clearFilterSheet}>{t('assets.clearFilters', 'Clear All')}</Button>
              <Button onClick={applyFilterSheet} style={{ background: 'var(--brand-primary,#0f172a)', color: '#fff' }}>
                {t('assets.applyFilters', 'Apply Filters')}
              </Button>
            </Group>
          </Box>
        </Drawer>

        {loading ? <Center p="xl"><Loader size="sm" color="teal" /></Center> : isMobile ? (
          // ── มือถือ: ออกแบบใหม่เป็นการ์ด ไม่ใช่ตารางเดิมย่อลงมา ── ตารางนี้มีข้อมูลเยอะ (ชื่อ+รุ่น,
          // ประเภท, ผู้ขาย, ที่นั่ง, วันหมดอายุ, ค่าใช้จ่ายรายปี, สถานะ) และปุ่มจัดการถึง 7 ปุ่ม —
          // เดิมอัดทุกอย่างในแถวเดียวยาวมาก บนมือถือจะล้นจอทันที ย้ายปุ่มทั้งหมดไปรวมในเมนู ⋮ (ยกเว้น
          // "View Details" ที่ตัดออกเพราะแตะที่การ์ดก็เปิด Quick View แบบเดียวกันอยู่แล้ว ไม่ต้องซ้ำ)
          filtered.length === 0 ? (
            <Center p="xl"><Text size="sm" c="dimmed">{t("license.noLicensesFound","No licenses found")}</Text></Center>
          ) : (
            <Stack gap="xs" p="xs">
              {filtered.map(l => (
                <Card key={l.id} withBorder radius="lg" p="md"
                  style={{ background: selectedIds.includes(l.id) ? '#fef2f2' : undefined, opacity: l.isActive === false ? 0.6 : 1 }}
                  onClick={() => { setQuickViewLicense(l); setQuickViewOpen(true) }}>
                  <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                    <Group gap={8} align="flex-start" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                      {isAdminLevel && (
                        <Checkbox size="xs" mt={3} onClick={(e) => e.stopPropagation()}
                          checked={selectedIds.includes(l.id)}
                          onChange={e => setSelectedIds(prev => e.currentTarget.checked ? [...prev, l.id] : prev.filter(id => id !== l.id))}
                        />
                      )}
                      {l.logoUrl ? (
                        <Image src={l.logoUrl} w={28} h={28} fit="contain" radius="sm" style={{ flexShrink: 0 }} />
                      ) : (
                        <Box w={28} h={28} style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 6, flexShrink: 0 }} />
                      )}
                      <Box style={{ minWidth: 0 }}>
                        <Group gap={6} wrap="wrap">
                          <Text size="sm" fw={600} style={{ color: '#0f172a', wordBreak: 'break-word' }}>{l.productName}</Text>
                          {l.type !== 'PERPETUAL' && (l.isOnward ? (
                            <Badge size="xs" color="cyan" variant="light" radius="sm">🔄 {t('license.onward','Onward')}</Badge>
                          ) : (
                            <Badge size="xs" color="teal" variant="light" radius="sm">
                              {t('licenses.yearLabel', { year: (l.renewals?.length ?? 0) + 1 })}
                            </Badge>
                          ))}
                        </Group>
                        {l.version && <Text size="xs" c="dimmed">v{l.version}</Text>}
                      </Box>
                    </Group>
                    <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                      <Badge size="sm" color={STATUS_COLOR[l.status] ?? 'gray'} variant="light">{l.status}</Badge>
                      <Menu shadow="md" width={200} position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray" size="lg"><IconDotsVertical size={18} /></ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item leftSection={<IconHistory size={15} />} onClick={() => handleOpenHistory(l)}>
                            {t('license.viewHistory','History')}
                          </Menu.Item>
                          <Menu.Item leftSection={<IconUserPlus size={15} />} onClick={() => handleAssign(l)}>
                            {t('licenses.assignTo','Assign to user/asset')}
                          </Menu.Item>
                          <Menu.Item leftSection={<IconRefresh size={15} />} onClick={() => handleRenew(l)}>
                            {t('common.renew','Renew')}
                          </Menu.Item>
                          <Menu.Item leftSection={<IconEdit size={15} />} onClick={() => handleOpenEdit(l)}>
                            {t('common.edit','Edit')}
                          </Menu.Item>
                          {l.isActive !== false ? (
                            <Menu.Item leftSection={<IconBan size={15} />} color="red"
                              onClick={() => modals.openConfirmModal({
                                title: t('license.deactivateTitle', 'Deactivate License'),
                                children: <Text size="sm">{t('license.deactivateBody', 'Deactivate {{name}}? It will be hidden from the main list but still viewable in the Inactive tab.', { name: l.productName })}</Text>,
                                labels: { confirm: t('common.deactivate', 'Deactivate'), cancel: t('common.cancel', 'Cancel') },
                                confirmProps: { color: 'red' },
                                onConfirm: async () => { await deactivateLicense(l.id); load() },
                              })}>
                              {t('common.deactivate','Deactivate')}
                            </Menu.Item>
                          ) : (
                            <Menu.Item leftSection={<IconCheck size={15} />} color="teal"
                              onClick={() => modals.openConfirmModal({
                                title: t('license.activateTitle', 'Activate License'),
                                children: <Text size="sm">{t('license.activateBody', 'Activate {{name}} back to Active?', { name: l.productName })}</Text>,
                                labels: { confirm: t('common.activate', 'Activate'), cancel: t('common.cancel', 'Cancel') },
                                confirmProps: { color: 'teal' },
                                onConfirm: async () => { await reactivateLicense(l.id); load() },
                              })}>
                              {t('common.reactivate','Activate กลับ')}
                            </Menu.Item>
                          )}
                          <Menu.Item leftSection={<IconTrash size={15} />} color="red" onClick={() => handleDeleteOne(l)}>
                            {t('config.deletePerm','Delete permanently')}
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                  </Group>

                  <Divider my={8} />

                  <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
                    <Group gap={6}>
                      <Badge size="sm" color={TYPE_COLOR[l.type] ?? 'gray'} variant="light">
                        {TYPE_ICON[l.type] ?? '📄'}{' '}
                        {l.type === 'PERPETUAL'    ? 'Perpetual'
                        : l.type === 'SUBSCRIPTION' ? 'Subscription'
                        : l.type === 'MA'           ? 'Maintenance'
                        : l.type === 'OEM'          ? 'OEM'
                        : l.type === 'LEASE'        ? 'Lease/Rental'
                        : l.type === 'SERVICE'      ? 'Service Agr.'
                        : l.type === 'HARDWARE'     ? 'Hardware Sup.'
                        : l.type === 'OTHER'        ? 'Other'
                        : l.type}
                      </Badge>
                      {l.vendor && <Text size="xs" c="dimmed">{l.vendor}</Text>}
                    </Group>
                    {(() => {
                      const annual = getAnnualCost(l)
                      return annual !== null
                        ? <Text size="xs" fw={500}>{l.currency ?? 'THB'} {annual.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Text>
                        : null
                    })()}
                  </Group>

                  <Group justify="space-between" align="flex-end" mt={8} wrap="wrap" gap="xs">
                    <Box>
                      <ExpiryBar l={l} />
                      {l.isOnward ? (
                        <Badge size="xs" color="cyan" variant="dot" mt={3}>
                          {l.billingCycle === 'MONTHLY'   ? '🔄 MONTHLY Onward' :
                           l.billingCycle === 'QUARTERLY' ? '🔄 QUARTERLY Onward' :
                           l.billingCycle === 'MULTI_YEAR' ? `🔄 ${l.contractYears}YR Onward` :
                           '🔄 YEARLY Onward'}
                        </Badge>
                      ) : l.billingCycle && l.billingCycle !== 'ANNUAL' && l.type !== 'PERPETUAL' ? (
                        <Badge size="xs" color="indigo" variant="dot" mt={3}>
                          {l.billingCycle === 'MONTHLY'    ? '📅 MONTHLY' :
                           l.billingCycle === 'QUARTERLY'  ? '📅 QUARTERLY' :
                           l.billingCycle === 'MULTI_YEAR' ? `📅 ${l.contractYears}YR DEAL` : ''}
                        </Badge>
                      ) : l.type !== 'PERPETUAL' ? (
                        <Badge size="xs" color="gray" variant="dot" mt={3}>📅 YEARLY</Badge>
                      ) : null}
                    </Box>
                    {l.totalSeats > 0 && (
                      <Box style={{ minWidth: 90 }}>
                        <Text size="xs" ta="right">{l.usedSeats}/{l.totalSeats} {t('license.colSeats','Seats')}</Text>
                        <Progress value={(l.usedSeats / l.totalSeats) * 100} size={3} color={l.usedSeats >= l.totalSeats ? 'red' : 'teal'} mt={3} />
                      </Box>
                    )}
                  </Group>
                </Card>
              ))}
            </Stack>
          )
        ) : (
          <Table highlightOnHover>
            <Table.Thead style={{ background: '#f8fafc' }}>
              <Table.Tr>
                {isAdminLevel && (
                  <Table.Th style={{ width: 36 }}>
                    <Checkbox size="xs"
                      checked={selectedIds.length === filtered.length && filtered.length > 0}
                      indeterminate={selectedIds.length > 0 && selectedIds.length < filtered.length}
                      onChange={e => setSelectedIds(e.currentTarget.checked ? filtered.map(l => l.id) : [])}
                    />
                  </Table.Th>
                )}
                {[
                  '', t('license.colProduct', 'Product'), t('common.type', 'Type'), t('common.vendor', 'Vendor'),
                  t('license.colSeats', 'Seats'), t('license.colExpiry', 'Expiry'), t('license.colAnnualCost', 'Annual Cost'), t('common.status', 'Status'), '',
                ].map((h, i) => (
                  <Table.Th key={i} style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{h}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.length === 0 ? (
                <Table.Tr><Table.Td colSpan={isAdminLevel ? 9 : 8}><Center p="xl"><Text size="sm" c="dimmed">{t("license.noLicensesFound","No licenses found")}</Text></Center></Table.Td></Table.Tr>
              ) : filtered.map(l => (
                <Table.Tr key={l.id}
                  style={{ background: selectedIds.includes(l.id) ? '#fef2f2' : undefined, cursor: 'pointer' }}
                  onClick={(e) => {
                    const target = e.target as HTMLElement
                    if (target.closest('input[type="checkbox"]') || target.closest('[data-action]')) return
                    setQuickViewLicense(l); setQuickViewOpen(true)
                  }}
                >
                  {isAdminLevel && (
                    <Table.Td data-action="true">
                      <Checkbox size="xs"
                        checked={selectedIds.includes(l.id)}
                        onChange={e => setSelectedIds(prev => e.currentTarget.checked ? [...prev, l.id] : prev.filter(id => id !== l.id))}
                      />
                    </Table.Td>
                  )}
                  <Table.Td>
                    {l.logoUrl ? (
                      <Image src={l.logoUrl} w={32} h={32} fit="contain" radius="sm" />
                    ) : (
                      <Box w={32} h={32} style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 6 }} />
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6} align="center" wrap="nowrap">
                      <Text size="sm" fw={600} style={{ color: '#0f172a' }}>{l.productName}</Text>
                      {l.type !== 'PERPETUAL' && (() => {
                        if (l.isOnward) {
                          // คำนวณปีที่จาก startDate
                          const yearNum = l.startDate
                            ? Math.floor((Date.now() - new Date(l.startDate).getTime()) / (365.25 * 24 * 3600 * 1000)) + 1
                            : null
                          return (
                            <Badge size="xs" color="cyan" variant="light" radius="sm">
                              🔄 {yearNum ? `Year ${yearNum}` : 'Onward'}
                            </Badge>
                          )
                        }
                        return (
                          <Badge size="xs" color="teal" variant="light" radius="sm">
                            {t('licenses.yearLabel', { year: (l.renewals?.length ?? 0) + 1 })}
                          </Badge>
                        )
                      })()}
                    </Group>
                    {l.version && <Text size="xs" c="dimmed">v{l.version}</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" color={TYPE_COLOR[l.type] ?? 'gray'} variant="light">
                      {TYPE_ICON[l.type] ?? '📄'}{' '}
                      {l.type === 'PERPETUAL'    ? 'Perpetual'
                      : l.type === 'SUBSCRIPTION' ? 'Subscription'
                      : l.type === 'MA'           ? 'Maintenance'
                      : l.type === 'OEM'          ? 'OEM'
                      : l.type === 'LEASE'        ? 'Lease/Rental'
                      : l.type === 'SERVICE'      ? 'Service Agr.'
                      : l.type === 'HARDWARE'     ? 'Hardware Sup.'
                      : l.type === 'OTHER'        ? 'Other'
                      : l.type}
                    </Badge>
                  </Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{l.vendor || '—'}</Text></Table.Td>
                  <Table.Td>
                    <Text size="sm">{l.totalSeats > 0 ? `${l.usedSeats}/${l.totalSeats}` : '∞'}</Text>
                    {l.totalSeats > 0 && (
                      <Progress value={(l.usedSeats / l.totalSeats) * 100} size={3} color={l.usedSeats >= l.totalSeats ? 'red' : 'teal'} mt={3} />
                    )}
                  </Table.Td>
                  <Table.Td>
                    <ExpiryBar l={l} />
                    {l.isOnward ? (
                      <Badge size="xs" color="cyan" variant="dot" mt={3}>
                        {l.billingCycle === 'MONTHLY'   ? '🔄 MONTHLY Onward' :
                         l.billingCycle === 'QUARTERLY' ? '🔄 QUARTERLY Onward' :
                         l.billingCycle === 'MULTI_YEAR' ? `🔄 ${l.contractYears}YR Onward` :
                         '🔄 YEARLY Onward'}
                      </Badge>
                    ) : l.billingCycle && l.billingCycle !== 'ANNUAL' && l.type !== 'PERPETUAL' ? (
                      <Badge size="xs" color="indigo" variant="dot" mt={3}>
                        {l.billingCycle === 'MONTHLY'    ? '📅 MONTHLY' :
                         l.billingCycle === 'QUARTERLY'  ? '📅 QUARTERLY' :
                         l.billingCycle === 'MULTI_YEAR' ? `📅 ${l.contractYears}YR DEAL` : ''}
                      </Badge>
                    ) : l.type !== 'PERPETUAL' ? (
                      <Badge size="xs" color="gray" variant="dot" mt={3}>📅 YEARLY</Badge>
                    ) : null}
                  </Table.Td>
                  <Table.Td>
                    {(() => {
                      const annual = getAnnualCost(l)
                      return annual !== null
                        ? <Text size="sm">{l.currency ?? 'THB'} {annual.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Text>
                        : <Text size="sm" c="dimmed">—</Text>
                    })()}
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" color={STATUS_COLOR[l.status] ?? 'gray'} variant="light">{l.status}</Badge>
                  </Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label={t("common.viewDetails","View details / assignments")}>
                        <ActionIcon size="sm" variant="subtle" color="blue" onClick={() => handleViewDetail(l)}>
                          <IconInfoCircle size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("license.viewHistory","History")}>
                        <ActionIcon size="sm" variant="subtle" color="indigo" onClick={() => handleOpenHistory(l)}>
                          <IconHistory size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("licenses.assignTo","Assign to user/asset")}>
                        <ActionIcon size="sm" variant="subtle" color="teal" onClick={() => handleAssign(l)}>
                          <IconUserPlus size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("common.renew","Renew")}>
                        <ActionIcon size="sm" variant="subtle" color="blue" onClick={() => handleRenew(l)}>
                          <IconRefresh size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("common.edit","Edit")}>
                        <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => handleOpenEdit(l)}>
                          <IconEdit size={14} />
                        </ActionIcon>
                      </Tooltip>
                      {l.isActive !== false ? (
                        <Tooltip label={t("common.deactivate","Deactivate")}>
                          <ActionIcon size="sm" variant="subtle" color="red"
                            onClick={() => modals.openConfirmModal({
                              title: t('license.deactivateTitle', 'Deactivate License'),
                              children: <Text size="sm">{t('license.deactivateBody', 'Deactivate {{name}}? It will be hidden from the main list but still viewable in the Inactive tab.', { name: l.productName })}</Text>,
                              labels: { confirm: t('common.deactivate', 'Deactivate'), cancel: t('common.cancel', 'Cancel') },
                              confirmProps: { color: 'red' },
                              onConfirm: async () => { await deactivateLicense(l.id); load() },
                            })}>
                            <IconBan size={14} />
                          </ActionIcon>
                        </Tooltip>
                      ) : (
                        <Tooltip label={t("common.reactivate","Activate กลับ")}>
                          <ActionIcon size="sm" variant="subtle" color="teal"
                            onClick={() => modals.openConfirmModal({
                              title: t('license.activateTitle', 'Activate License'),
                              children: <Text size="sm">{t('license.activateBody', 'Activate {{name}} back to Active?', { name: l.productName })}</Text>,
                              labels: { confirm: t('common.activate', 'Activate'), cancel: t('common.cancel', 'Cancel') },
                              confirmProps: { color: 'teal' },
                              onConfirm: async () => { await reactivateLicense(l.id); load() },
                            })}>
                            <IconCheck size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label={t("config.deletePerm","Delete permanently")}>
                        <ActionIcon size="sm" variant="subtle" color="red"
                          onClick={() => handleDeleteOne(l)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody></Table>
        )}
      </Box>

      {/* Detail Modal */}
      {/* ── License Quick View Drawer ── */}
      <LicenseQuickView
        license={quickViewLicense}
        opened={quickViewOpen}
        onClose={() => setQuickViewOpen(false)}
        onEdit={(l) => { handleOpenEdit(l) }}
        onRenew={(l) => { handleRenew(l) }}
        onDelete={(l) => { setQuickViewOpen(false); handleDeleteOne(l) }}
        onRefresh={async () => { const res = await getLicenses(); setLicenses(res.data) }}
      />


      {/* Create/Edit Modal */}
      <Modal opened={formOpened} onClose={closeForm} size="lg" radius="lg" 
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 15 }}>{editing ? t('licenses.editLicense','Edit License') : t('licenses.addLicense','Add License')}</Text>}>
        <Stack gap="sm">
          {/* ── Logo (200×200, ไม่บังคับใส่) — ไม่ใส่ = โชว์เป็นช่องว่าง/placeholder ในรายการ ──
          pattern เดียวกับ Model Image ใน ConfigurationPage.tsx ทุกประการ แค่ crop แบบ 'fit' +
          พื้นหลังขาวแทน 'fill' เพราะโลโก้ vendor ส่วนใหญ่ไม่ใช่สี่เหลี่ยมจัตุรัส ไม่อยากครอปขอบขาด */}
          <Box>
            <Text size="sm" fw={600} mb={6}>{t("licenses.logoLabel","Logo")} <Text span size="xs" c="dimmed">{t("licenses.logoOptionalHint","(optional — helps you spot it in the list)")}</Text></Text>
            <Group align="flex-start" gap="md">
              <Box w={64} h={64} style={{ background: '#fff', borderRadius: 10, border: '1.5px dashed #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                {logoPreview ? (
                  <Image src={logoPreview} w={64} h={64} fit="contain" />
                ) : (
                  <IconPhoto size={22} color="#ccc" />
                )}
              </Box>
              <Stack gap={6} style={{ flex: 1 }}>
                <FileButton
                  onChange={(file) => {
                    if (!file) return
                    setLogoFile(file)
                    setLogoPreview(URL.createObjectURL(file))
                  }}
                  accept="image/png,image/jpeg,image/webp"
                >
                  {(props) => (
                    <Button {...props} variant="outline" size="xs" leftSection={<IconUpload size={14} />} color="gray">
                      {logoPreview ? t('licenses.changeLogo', 'Change Logo') : t('licenses.chooseLogo', 'Choose Logo')}
                    </Button>
                  )}
                </FileButton>
                {logoPreview && (
                  <ActionIcon
                    size="xs" color="red" variant="subtle" w="fit-content"
                    onClick={async () => {
                      if (editing?.id && !logoFile) {
                        await deleteLicenseLogo(editing.id)
                        notifications.show({ color: 'lime', message: t('licenses.logoRemoved', 'Logo removed') })
                        load()
                      }
                      setLogoFile(null); setLogoPreview(null)
                    }}>
                    <IconTrash size={14} />
                  </ActionIcon>
                )}
              </Stack>
            </Group>
          </Box>

          {/* Department field */}
          {isAdminLevel ? (
            <Select
              label={t('licenses.department','Department')}
              placeholder={t('licenses.selectDept','Select department...')}
              data={deptOptions}
              value={form.departmentCode || null}
              onChange={v => f('departmentCode')(v ?? '')}
              searchable clearable
              description={t('licenses.deptHintGlobal','Leave blank for global / shared license')}
            />
          ) : isScopedToDepts ? (
            scopedDepts.length > 1 ? (
              <Select
                label={t('licenses.department','Department')}
                placeholder={t('licenses.selectDept','Select department...')}
                data={deptOptions.filter(d => scopedDepts.includes(d.value))}
                value={form.departmentCode || null}
                onChange={v => f('departmentCode')(v ?? '')}
                searchable
                description={t('licenses.deptHintOwn','Select from your departments')}
              />
            ) : (
              <TextInput
                label={t('licenses.department','Department')}
                value={deptOptions.find(d => d.value === scopedDepts[0])?.label ?? scopedDepts[0]}
                readOnly
                styles={{ input: { background: '#f8fafc', fontWeight: 600 } }}
                description={t('licenses.deptAutoHint','Auto-assigned to your department')}
              />
            )
          ) : null}
          <Divider label={t("licenses.product","Product")} labelPosition="left" />
          <SimpleGrid cols={isSmall ? 1 : 2}>
            <TextInput label={t("licenses.productName","Product Name *")} placeholder={t('licenses.productNamePlaceholder','e.g. Microsoft 365')}
              value={form.productName}
              onChange={e => f('productName')(e.target.value)}
            />
            <Group gap={4} align="flex-end">
              <Box style={{ flex: 1 }}>
                <Select
                  label={t('licenses.vendor','Vendor')}
                  placeholder={t('licenses.vendorPlaceholder','Select vendor...')}
                  data={vendorOptions}
                  value={form.vendor || null}
                  onChange={v => f('vendor')(v ?? '')}
                  searchable
                  clearable
                />
              </Box>
              <Tooltip label={t('licenses.addVendorTooltip', 'Add new Vendor')}>
                <ActionIcon variant="light" color="teal" size="lg" mb={1} onClick={openQAVendor}>
                  <IconPlus size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
            <TextInput label={t("licenses.version","Version")} placeholder={t('licenses.versionPlaceholder','e.g. 2024')} value={form.version} onChange={e => f('version')(e.target.value)} />
            <Select label={t('licenses.type','Type')} value={form.type} onChange={f('type')}
              data={[
                { group: t('licenses.typeSubGroup','License / Subscription'), items: [
                  { value: 'SUBSCRIPTION', label: t('licenses.typeSubOpt','🔄 Subscription (Annual/Yearly)') },
                  { value: 'PERPETUAL',    label: t('licenses.typePerpOpt','🔑 Perpetual (one-time purchase)') },
                  { value: 'OEM',          label: t('licenses.typeOemOpt','💻 OEM (bundled with hardware)') },
                ]},
                { group: t('licenses.typeContractGroup','Contract'), items: [
                  { value: 'MA',           label: t('licenses.typeMaOpt','🛠 Maintenance Agreement') },
                  { value: 'LEASE',        label: t('licenses.typeLeaseOpt','📦 Lease / Rental') },
                  { value: 'SERVICE',      label: t('licenses.typeServiceOpt','⚙️ Service Agreement') },
                  { value: 'HARDWARE',     label: t('licenses.typeHwOpt','🖥 Hardware Support Contract') },
                  { value: 'OTHER',        label: t('licenses.typeOtherOpt','📄 Other Contract') },
                ]},
              ]} />
          </SimpleGrid>

          <Divider label={t("licenses.licenseDetails","License Details")} labelPosition="left" />
          <SimpleGrid cols={2}>
            <TextInput label={t("licenses.licenseKey","License Key")} placeholder={t('licenses.licenseKeyPlaceholder','XXXX-XXXX-XXXX')} value={form.licenseKey} onChange={e => f('licenseKey')(e.target.value)} />
            <TextInput label={t("licenses.orderNumber","Order / PO Number")} value={form.orderNumber} onChange={e => f('orderNumber')(e.target.value)} />
            <NumberInput label={t("licenses.totalSeats","Total Seats (0 = unlimited)")} min={0} value={form.totalSeats} onChange={f('totalSeats')} />
            <Group gap={6} align="flex-end">
              {!editing && (
                <Box style={{ flex: 1 }}>
                  <NumberInput label={t('licenses.cost','Cost — ราคาไม่รวม VAT')}
                    min={0} decimalScale={2} value={form.cost} onChange={f('cost')} />
                </Box>
              )}
              <Box style={{ width: editing ? '100%' : 100 }}>
                <Select label={t('licenses.currency','Currency')} allowDeselect={false}
                  value={form.currency ?? 'THB'}                  onChange={v => f('currency')(v ?? 'THB')}
                  data={['THB','USD','EUR','JPY','SGD','GBP'].map(c => ({ value: c, label: c }))}
                />
              </Box>
            </Group>
          </SimpleGrid>

          {/* ── ราคาไม่ได้แก้ผ่านฟอร์มนี้อีกต่อไปเลยเมื่อเป็นการแก้ไข license ที่มีอยู่แล้ว — ทั้งราคา
          ปัจจุบันและราคาตั้งต้นอยู่ในไทม์ไลน์เดียวกัน (แท็บ "การต่ออายุ") แก้ไขที่นั่นโดยตรง กด
          ปุ่มดินสอที่รายการไหนก็ได้ รวมถึงรายการ "Original" เอง ── */}
          {editing && (
            <Box p="sm" style={{ background: '#f0fdfa', borderRadius: 10, border: '1px solid #99f6e4' }}>
              <Text size="xs" c="teal.9">
                💡 {t('licenses.priceEditsMoved', 'Price is no longer edited here — go to the Renewals tab and edit any entry (including the Original one) to correct a price.')}
              </Text>
            </Box>
          )}

          {/* ── Pricing Model ── */}
          {form.type !== 'PERPETUAL' && (
            <Box p="sm" style={{ background: '#f1f5f9', borderRadius: 10, border: '1px solid #e2e8f0' }}>
              <Text size="sm" fw={600} mb={10}>💰 {t("licenses.pricingModel","Pricing Model")}</Text>
              <Group gap={10} mb={form.pricingModel === 'SCHEDULE' ? 'md' : 0}>
                {[
                  { value: 'FIXED',    label: `💵 ${t('licenses.fixedPrice','Fixed Price')}`, desc: t('licenses.fixedPriceDesc','ราคาเดียว (Monthly/Quarterly/Yearly)') },
                  { value: 'SCHEDULE', label: `📋 ${t('licenses.paymentSchedule','Payment Schedule')}`, desc: t('licenses.paymentScheduleDesc','ชำระหลายงวด ยอดแต่ละงวดต่างกันได้') },
                ].map(opt => (
                  <Box key={opt.value}
                    onClick={() => f('pricingModel')(opt.value)}
                    style={{
                      flex: 1, padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                      border: `2px solid ${form.pricingModel === opt.value ? '#6366f1' : '#e2e8f0'}`,
                      background: form.pricingModel === opt.value ? '#eef2ff' : '#fff',
                      transition: 'all 0.15s',
                    }}>
                    <Text size="sm" fw={600} c={form.pricingModel === opt.value ? '#4f46e5' : '#0f172a'}>{opt.label}</Text>
                    <Text size="xs" c="dimmed">{opt.desc}</Text>
                  </Box>
                ))}
              </Group>

              {form.pricingModel === 'SCHEDULE' && (
                <Box>
                  {(form.paymentSchedule ?? []).map((pay: any, idx: number) => (
                    <Paper key={pay._key ?? idx} withBorder radius="md" p="sm" mb="xs"
                      style={{ border: '1px solid #e2e8f0', background: '#fff' }}>
                      <Group justify="space-between" mb={6}>
                        <Text size="xs" fw={600} c="dimmed">{t("licenses.installment","งวดที่")} {idx + 1}</Text>
                        <ActionIcon size="xs" color="red" variant="subtle"
                          onClick={() => f('paymentSchedule')(form.paymentSchedule.filter((_: any, i: number) => i !== idx))}>
                          <IconX size={12} />
                        </ActionIcon>
                      </Group>
                      <Group grow gap={8} wrap="wrap">
                        <TextInput size="xs" label={t("licenses.paymentDesc","รายละเอียด")} placeholder={t("licenses.paymentDescPlaceholder","เช่น ปีที่ 1, Q3 2026")}
                          value={pay.description ?? ''}
                          onChange={e => f('paymentSchedule')(form.paymentSchedule.map((r: any, i: number) => i === idx ? { ...r, description: e.target.value } : r))} />
                        <SmartDateInput label={t("licenses.dueDate","วันครบกำหนด")} value={pay.dueDate ?? ''}
                          onChange={(v: string) => f('paymentSchedule')(form.paymentSchedule.map((r: any, i: number) => i === idx ? { ...r, dueDate: v } : r))} />
                        <NumberInput size="xs" label={t("licenses.amount","จำนวนเงิน")} min={0} thousandSeparator=","
                          value={pay.amount ?? 0}
                          onChange={(v: any) => f('paymentSchedule')(form.paymentSchedule.map((r: any, i: number) => i === idx ? { ...r, amount: Number(v) } : r))} />
                        <Select size="xs" label={t("licenses.frequency","ความถี่")} allowDeselect={false}
                          data={[{ value:'ONE_TIME',label:'One-time'},{value:'MONTHLY',label:'Monthly'},{value:'QUARTERLY',label:'Quarterly'},{value:'YEARLY',label:'Yearly'}]}
                          value={pay.frequency ?? 'YEARLY'}
                          onChange={(v: any) => f('paymentSchedule')(form.paymentSchedule.map((r: any, i: number) => i === idx ? { ...r, frequency: v } : r))} />
                      </Group>
                    </Paper>
                  ))}
                  <Group justify="space-between" mt="xs">
                    <Button variant="subtle" size="xs" leftSection={<IconPlus size={12} />}
                      onClick={() => f('paymentSchedule')([...( form.paymentSchedule ?? []), { description:'', dueDate:'', amount:0, frequency:'YEARLY', _key: Date.now() }])}>
                      {t('licenses.addPayment','+ Add Payment')}
                    </Button>
                    {(form.paymentSchedule ?? []).length > 0 && (
                      <Badge color="violet" variant="light" size="sm">
                        {t("licenses.total","รวม")}: {form.currency} {(form.paymentSchedule ?? []).reduce((s: number, p: any) => s + Number(p.amount || 0), 0).toLocaleString()}
                      </Badge>
                    )}
                  </Group>
                </Box>
              )}
            </Box>
          )}

          {/* ── Billing Cycle (Fixed Price only) ── */}
          {form.type !== 'PERPETUAL' && form.pricingModel !== 'SCHEDULE' && (
            <Box p="sm" style={{ background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
              <Text size="sm" fw={600} mb={10}>💳 {t('licenses.billingCycle', 'Billing Cycle')}</Text>
              <SimpleGrid cols={form.billingCycle === 'MULTI_YEAR' ? 3 : 2}>
                <Select label={t('licenses.billingCycleLabel', 'Payment Type')}
                  value={form.billingCycle}
                  onChange={v => f('billingCycle')(v ?? 'ANNUAL')}
                  data={[
                    { value: 'MONTHLY',    label: `📅 ${t('licenses.billingMonthly', 'Monthly — Pay monthly')}` },
                    { value: 'QUARTERLY',  label: `📅 ${t('licenses.billingQuarterly', 'Quarterly — Pay quarterly')}` },
                    { value: 'ANNUAL',     label: `📅 ${t('licenses.billingAnnual', 'Annual — Pay yearly')}` },
                    { value: 'MULTI_YEAR', label: `📅 ${t('licenses.billingMultiYear', 'Multi-year — Single payment')}` },
                  ]} />
                {form.billingCycle === 'MULTI_YEAR' && (
                  <NumberInput label={t('licenses.contractYears', 'Contract Years')} min={2} max={10}
                    value={form.contractYears} onChange={v => f('contractYears')(v ?? 1)} />
                )}
                <Box>
                  <Text size="xs" c="dimmed" mb={4}>{t('licenses.annualCostCalc', 'Annual Cost (auto-calculated)')}</Text>
                  <Text fw={700} size="sm" c="teal.7">
                    {form.currency ?? 'THB'} {(() => {
                      const cost = Number(form.cost ?? 0)
                      switch (form.billingCycle) {
                        case 'MONTHLY':    return (cost * 12).toLocaleString()
                        case 'QUARTERLY':  return (cost * 4).toLocaleString()
                        case 'MULTI_YEAR': return (cost / Math.max(1, Number(form.contractYears ?? 1))).toLocaleString()
                        default:           return cost.toLocaleString()
                      }
                    })()} {t('licenses.perYear', '/ year')}
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>
          )}

          <Divider label={t("licenses.dates","Dates")} labelPosition="left" />
          <SimpleGrid cols={3}>
            <SmartDateInput label={t("licenses.purchaseDate","Purchase Date")} placeholder={t("license.datePlaceholder","DD/MM/YYYY")} value={form.purchaseDate} onChange={v => f('purchaseDate')(v)} clearable />
            <SmartDateInput label={t("licenses.startDate","Start Date")}    placeholder={t("license.datePlaceholder","DD/MM/YYYY")} value={form.startDate}    onChange={v => f('startDate')(v)}    clearable />
            <Box>
              <SmartDateInput label={t("licenses.expiryDate","Expiry Date")} placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
                value={form.isOnward ? '' : form.expiryDate}
                onChange={v => f('expiryDate')(v)} clearable
                disabled={form.isOnward} />
              <Checkbox mt={6} size="xs"
                label={t("licenses.noFixedExpiry","🔄 No Fixed Expiry (Onward / Auto-renew)")}
                checked={form.isOnward ?? false}
                onChange={e => { f('isOnward')(e.currentTarget.checked); if (e.currentTarget.checked) f('expiryDate')('') }}
              />
            </Box>
          </SimpleGrid>

          <Divider label={t("licenses.notifications","Notifications")} labelPosition="left" />
          <SimpleGrid cols={2}>
            <TextInput label={t("licenses.managedBy","Managed By")} value={form.managedBy} onChange={e => f('managedBy')(e.target.value)} />
            <TextInput label={t("licenses.notifyEmail","Notify Email")} type="email" placeholder="admin@company.com" value={form.notifyEmail} onChange={e => f('notifyEmail')(e.target.value)} />
          </SimpleGrid>
          <TextInput label={t("licenses.reminderDays","Reminder Days (comma-separated)")} placeholder="60,30,7"
            value={(form.reminderDays ?? []).join(',')}
            onChange={e => {
              const vals = e.target.value.split(',').map(v => parseInt(v.trim())).filter(n => !isNaN(n))
              f('reminderDays')(vals)
            }} />

          <Textarea label={t("licenses.notes","Notes")} placeholder={t("licenses.notesPlaceholder","Additional info, contract details, etc.")} value={form.notes} onChange={e => f('notes')(e.target.value)} />

          {/* Attachments — only show on Create (Edit: use Attachments tab in detail view) */}
          {!editing && (
            <Box>
              <Divider label={t("licenses.attachments","Attachments (optional)")} labelPosition="left" mb="xs" />
              <Text size="xs" c="dimmed" mb={8}>{t('licenses.attachFilesHint', 'Attach Invoice, Contract, PO or related documents')}</Text>
              <Group gap={8} wrap="wrap">
                {licenseFileNames.map((name, i) => (
                  <Badge key={i} color="teal" variant="light" size="sm"
                    rightSection={
                      <ActionIcon size={12} variant="transparent" color="teal"
                        onClick={() => { setLicenseFiles(p => p.filter((_, j) => j !== i)); setLicenseFileNames(p => p.filter((_, j) => j !== i)) }}>
                        <IconX size={10} />
                      </ActionIcon>
                    }>
                    {name}
                  </Badge>
                ))}
                <FileButton onChange={(file) => {
                  if (!file) return
                  setLicenseFiles(p => [...p, file])
                  setLicenseFileNames(p => [...p, file.name])
                }} accept="*/*">
                  {(props) => (
                    <Button {...props} size="xs" variant="light" color="teal" leftSection={<IconPaperclip size={13} />}>
                      {t("licenses.addFile","Add File")}
                    </Button>
                  )}
                </FileButton>
              </Group>
            </Box>
          )}

          <Divider />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeForm}>{t("licenses.cancel","Cancel")}</Button>
            <Button loading={saving} onClick={handleSave}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              {editing ? t('licenses.saveChanges', 'Save Changes') : t('licenses.createLicense', 'Create License')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Assign License Modal ── */}
      <Modal opened={assignModalOpen} onClose={closeAssignModal} size="lg" radius="lg"
        title={
          <Text fw={600} style={{ fontFamily: 'Inter,sans-serif', fontSize: 15 }}>
            Assign — {assignLicenseItem?.productName}
          </Text>
        }>
        <Stack gap="sm">
          {assignLicenseItem && (
            <Box style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '8px 14px' }}>
              <Group justify="space-between">
                <Text size="xs" c="teal" fw={600}>
                  Available seats: {assignLicenseItem.totalSeats > 0 ? assignLicenseItem.availableSeats : '∞'}
                </Text>
                {assignTargetIds.length > 0 && (
                  <Text size="xs" c="teal" fw={600}>{assignTargetIds.length} selected</Text>
                )}
              </Group>
            </Box>
          )}

          <Select
            label={t("license.assignTo","Assign to")}
            data={[
              { value: 'USER',  label: '👤 Recipient' },
              { value: 'ASSET', label: '💻 Asset' },
            ]}
            value={assignTargetType}
            onChange={v => {
              setAssignTargetType(v ?? 'USER')
              setAssignTargetIds([])
              setAssignTargetNames({})
              setAssignSearch('')
            }}
          />

          {/* Search */}
          <TextInput
            placeholder={assignTargetType === 'ASSET' ? 'Search asset code or name...' : 'Search recipient...'}
            value={assignSearch}
            onChange={e => setAssignSearch(e.target.value)}
            leftSection={<Text size="xs">🔍</Text>}
          />

          {/* Card Grid */}
          <ScrollArea h={280}>
            {(() => {
              const alreadyAssigned = new Set(
                (assignLicenseItem?.assignments ?? []).map((a: any) => a.targetId)
              )
              const allItems = assignTargetType === 'ASSET' ? allAssets : allRecipients
              const filtered = allItems.filter((item: any) => {
                const q = assignSearch.toLowerCase()
                if (!q) return true
                if (assignTargetType === 'ASSET')
                  return item.assetCode?.toLowerCase().includes(q) || item.name?.toLowerCase().includes(q)
                return item.name?.toLowerCase().includes(q) || item.position?.toLowerCase().includes(q)
              })
              const available = filtered.filter((item: any) => {
                const id = assignTargetType === 'ASSET' ? item.assetCode : item.name
                return !alreadyAssigned.has(id)
              })
              const assigned = filtered.filter((item: any) => {
                const id = assignTargetType === 'ASSET' ? item.assetCode : item.name
                return alreadyAssigned.has(id)
              })
              return (
                <Stack gap="xs">
                  {/* Available items */}
                  <SimpleGrid cols={2} spacing="xs">
                    {available.map((item: any) => {
                      const id = assignTargetType === 'ASSET' ? item.assetCode : item.name
                      const isSelected = assignTargetIds.includes(id)
                      return (
                        <Box key={id}
                          onClick={() => {
                            setAssignTargetIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
                            setAssignTargetNames(prev => ({ ...prev, [id]: item.name }))
                          }}
                          style={{
                            padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                            border: `1.5px solid ${isSelected ? '#0d9488' : '#e2e8f0'}`,
                            background: isSelected ? '#f0fdf4' : '#fff',
                            transition: 'all 0.1s',
                          }}>
                          <Group gap={8} wrap="nowrap">
                            <Text size="lg">{assignTargetType === 'ASSET' ? '💻' : '👤'}</Text>
                            <Box style={{ minWidth: 0, flex: 1 }}>
                              <Group gap={4} wrap="nowrap">
                                <Text size="xs" fw={600} lineClamp={1}>
                                  {assignTargetType === 'ASSET' ? item.assetCode : item.name}
                                </Text>
                                {assignTargetType === 'ASSET' && item.status && item.status !== 'AVAILABLE' && (
                                  <Badge size="xs" color={
                                    item.status === 'REPAIR'  ? 'orange' :
                                    item.status === 'DAMAGED' ? 'red' :
                                    item.status === 'LOST'    ? 'yellow' : 'gray'
                                  } variant="light" style={{ flexShrink: 0 }}>{item.status}</Badge>
                                )}
                              </Group>
                              <Text size="xs" c="dimmed" lineClamp={1}>
                                {assignTargetType === 'ASSET' ? item.name : (item.position || item.department || '')}
                              </Text>
                            </Box>
                            {isSelected && <Text size="sm" c="teal" style={{ flexShrink: 0 }}>✓</Text>}
                          </Group>
                        </Box>
                      )
                    })}
                  </SimpleGrid>

                  {/* Already assigned items */}
                  {assigned.length > 0 && (
                    <>
                      <Text size="xs" c="dimmed" fw={500} mt={4}>Already assigned ({assigned.length})</Text>
                      <SimpleGrid cols={2} spacing="xs">
                        {assigned.map((item: any) => {
                          const id = assignTargetType === 'ASSET' ? item.assetCode : item.name
                          return (
                            <Box key={id} style={{
                              padding: '8px 10px', borderRadius: 8,
                              border: '1.5px dashed #e2e8f0',
                              background: '#f8fafc', opacity: 0.6,
                            }}>
                              <Group gap={8} wrap="nowrap">
                                <Text size="lg" style={{ opacity: 0.5 }}>{assignTargetType === 'ASSET' ? '💻' : '👤'}</Text>
                                <Box style={{ minWidth: 0, flex: 1 }}>
                                  <Group gap={4} wrap="nowrap">
                                    <Text size="xs" fw={600} c="dimmed" lineClamp={1}>
                                      {assignTargetType === 'ASSET' ? item.assetCode : item.name}
                                    </Text>
                                    {assignTargetType === 'ASSET' && item.status && item.status !== 'AVAILABLE' && (
                                      <Badge size="xs" color={
                                        item.status === 'REPAIR'  ? 'orange' :
                                        item.status === 'DAMAGED' ? 'red' :
                                        item.status === 'LOST'    ? 'yellow' : 'gray'
                                      } variant="light" style={{ flexShrink: 0 }}>{item.status}</Badge>
                                    )}
                                  </Group>
                                  <Text size="xs" c="dimmed" lineClamp={1}>
                                    {assignTargetType === 'ASSET' ? item.name : (item.position || item.department || '')}
                                  </Text>
                                </Box>
                                <Badge size="xs" color="gray" variant="light" style={{ flexShrink: 0 }}>{t("license.assigned","Assigned")}</Badge>
                              </Group>
                            </Box>
                          )
                        })}
                      </SimpleGrid>
                    </>
                  )}
                </Stack>
              )
            })()}
          </ScrollArea>

          <Textarea
            label={t("common.note","Note")}
            placeholder={t('licenses.noteLabel', 'Note')}
            minRows={2}
            value={assignNote}
            onChange={e => setAssignNote(e.target.value)}
          />
          <Group justify="flex-end" pt="xs">
            <Button variant="default" onClick={closeAssignModal}>{t("common.cancel","Cancel")}</Button>
            <Button
              disabled={!assignTargetIds.length}
              onClick={confirmAssign}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              Assign {assignTargetIds.length > 0 ? `(${assignTargetIds.length})` : ''}
            </Button>
          </Group>
        </Stack>
      </Modal>
        

      {/* ── Quick Add Vendor Modal ── */}
      <Modal opened={qaVendorOpen} onClose={closeQAVendor} size="sm" radius="lg"
        title={<Text fw={600} style={{ fontFamily: 'Inter,sans-serif', fontSize: 15 }}>+ เพิ่ม Vendor ใหม่</Text>}>
        <Stack gap="sm">
          <TextInput
            label="Vendor Name *"
            placeholder="e.g. Microsoft, Adobe, Oracle"
            value={qaVendorName}
            onChange={e => setQAVendorName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleQuickAddVendor()}
            autoFocus
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeQAVendor}>{t('licenses.cancelBtn', 'Cancel')}</Button>
            <Button loading={qaVendorSaving} onClick={handleQuickAddVendor}
              disabled={!qaVendorName.trim()}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              บันทึก
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Renew Modal ── */}
      {renewTarget && (
        <Modal opened={renewOpened} onClose={closeRenew} size="md" radius="lg"
          title={<Text fw={600}>Renew — {renewTarget.productName}</Text>}>
          <Stack gap="sm">
            <Text size="sm" c="dimmed">Current expiry: <b>{renewTarget.expiryDate?.slice(0,10) ?? 'None'}</b></Text>
            {renewTarget.billingCycle && renewTarget.billingCycle !== 'ANNUAL' && (
              <Box p="xs" style={{ background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                <Text size="xs" c="teal.7" fw={500}>
                  💳 {renewTarget.billingCycle === 'MONTHLY' ? 'Monthly billing — next expiry auto-set to +1 month' :
                       renewTarget.billingCycle === 'QUARTERLY' ? 'Quarterly billing — next expiry auto-set to +3 months' :
                       renewTarget.billingCycle === 'MULTI_YEAR' ? `Multi-year contract — next expiry auto-set to +${renewTarget.contractYears ?? 1} year(s)` : ''}
                </Text>
              </Box>
            )}
            <SmartDateInput label={t("license.newStartDate","New start date")} placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
              value={renewForm.renewedFrom} onChange={v => setRenewForm(p => ({ ...p, renewedFrom: v }))} />
            <SmartDateInput label="New expiry date *" placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
              value={renewForm.renewedTo} onChange={v => setRenewForm(p => ({ ...p, renewedTo: v }))} />
            <Select label={t("common.vendor","Vendor")} placeholder="Select vendor (if changed)"
              data={vendorOptions} value={renewForm.vendor || null}
              onChange={v => setRenewForm(p => ({ ...p, vendor: v ?? '' }))}
              searchable clearable />
            <NumberInput label={t("license.renewalCostLabel", "Renewal cost (฿) *")} min={0}
              placeholder={t("license.renewalCostPlaceholder", "Enter this renewal's actual price")}
              value={renewForm.cost ?? ''} onChange={v => setRenewForm(p => ({ ...p, cost: v === '' ? null : Number(v) }))} />
            <TextInput label={t("license.orderPoNumber","Order / PO number")}
              value={renewForm.orderNumber} onChange={e => setRenewForm(p => ({ ...p, orderNumber: e.target.value }))} />
            <Textarea label={t("common.note","Note")} minRows={2}
              value={renewForm.note} onChange={e => setRenewForm(p => ({ ...p, note: e.target.value }))} />

            {/* Attachment upload */}
            <Box>
              <Text size="sm" fw={500} mb={6}>Attach Documents (optional)</Text>
              <Text size="xs" c="dimmed" mb={8}>Invoice, contract, PO — จะแนบไว้ใน Renewal record นี้</Text>
              <Group gap={8} wrap="wrap">
                {renewFileNames.map((name, i) => (
                  <Badge key={i} color="teal" variant="light" size="sm"
                    rightSection={
                      <ActionIcon size={12} variant="transparent" color="teal"
                        onClick={() => { setRenewFiles(p => p.filter((_, j) => j !== i)); setRenewFileNames(p => p.filter((_, j) => j !== i)) }}>
                        <IconX size={10} />
                      </ActionIcon>
                    }>
                    {name}
                  </Badge>
                ))}
                <FileButton onChange={(file) => {
                  if (!file) return
                  setRenewFiles(p => [...p, file])
                  setRenewFileNames(p => [...p, file.name])
                }} accept="*/*">
                  {(props) => (
                    <Button {...props} size="xs" variant="light" color="teal" leftSection={<IconPaperclip size={13} />}>
                      {t("licenses.addFile","Add File")}
                    </Button>
                  )}
                </FileButton>
              </Group>
            </Box>

            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={closeRenew}>{t("common.cancel","Cancel")}</Button>
              <Button color="blue" loading={renewing} onClick={handleRenewSubmit}>{t("license.renew","Renew")}</Button>
            </Group>
          </Stack>
        </Modal>
      )}

      {/* ── Edit Renewal Modal ── */}
      {editRenewal && (
        <Modal opened={editRenewalOpened} onClose={closeEditRenewal} size="md" radius="lg"
          title={<Text fw={600}>{t("licenses.editRenewal","Edit Renewal")}</Text>}>
          <Stack gap="sm">
            <SmartDateInput label={t("licenses.startDate","Start date")} placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
              value={editRenewalForm.renewedFrom} onChange={v => setEditRenewalForm(p => ({ ...p, renewedFrom: v }))} />
            <SmartDateInput label={t("licenses.expiryDate","Expiry date")+' *'} placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
              value={editRenewalForm.renewedTo} onChange={v => setEditRenewalForm(p => ({ ...p, renewedTo: v }))} />
            <Select label={t("common.vendor","Vendor")} data={vendorOptions} searchable clearable
              value={editRenewalForm.vendor || null}
              onChange={v => setEditRenewalForm(p => ({ ...p, vendor: v ?? '' }))} />
            <NumberInput label="Cost (฿)" min={0}
              value={editRenewalForm.cost} onChange={v => setEditRenewalForm(p => ({ ...p, cost: Number(v) }))} />
            <TextInput label={t("license.orderPoNumber","Order / PO number")}
              value={editRenewalForm.orderNumber} onChange={e => setEditRenewalForm(p => ({ ...p, orderNumber: e.target.value }))} />
            <Textarea label={t("common.note","Note")} minRows={2}
              value={editRenewalForm.note} onChange={e => setEditRenewalForm(p => ({ ...p, note: e.target.value }))} />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={closeEditRenewal}>{t("common.cancel","Cancel")}</Button>
              <Button color="blue" loading={savingRenewal} onClick={async () => {
                if (!editRenewalForm.renewedTo) { notifications.show({ color: 'red', message: 'Expiry date required' }); return }
                setSavingRenewal(true)
                try {
                  await updateRenewal(editRenewal.id, editRenewalForm)
                  notifications.show({ color: 'teal', message: 'Renewal updated' })
                  closeEditRenewal()
                  // Refresh detail
                  setDetailLicense((prev: any) => ({
                    ...prev,
                    renewals: prev.renewals.map((rn: any) => rn.id === editRenewal.id ? { ...rn, ...editRenewalForm } : rn)
                  }))
                  load()
                } catch { notifications.show({ color: 'red', message: 'Update failed' }) }
                finally { setSavingRenewal(false) }
              }}>{t("common.saveChanges","Save Changes")}</Button>
            </Group>
          </Stack>
        </Modal>
      )}

      {/* ── Contracts Section ── */}
      {contracts.length > 0 && (
        <Box mt="xl">
          <Group justify="space-between" mb="sm">
            <Group gap={8}>
              <IconCoin size={18} color="#6366f1" />
              <Text fw={600} size="sm">{t("license.contracts","Contracts")}</Text>
              {contractSummary && (
                <>
                  <Badge color="teal" variant="light" size="sm">{contractSummary.active} Active</Badge>
                  {contractSummary.expiring > 0 && <Badge color="orange" variant="light" size="sm">{contractSummary.expiring} Expiring</Badge>}
                  <Badge color="violet" variant="light" size="sm">
                    {new Date().getFullYear()} Cost: ฿{Number(contractSummary.currentYearCost ?? 0).toLocaleString()}
                  </Badge>
                </>
              )}
            </Group>
            <Button size="xs" variant="subtle" leftSection={<IconPlus size={12} />} onClick={handleOpenNewContract}>
              {t('licenses.addContract', 'Add Contract')}
            </Button>
          </Group>
          <ScrollArea>
            <Table highlightOnHover>
              <Table.Thead style={{ background: '#f8fafc' }}>
                <Table.Tr>
                  <Table.Th>{t("license.contract","Contract")}</Table.Th>
                  <Table.Th>{t("common.vendor","Vendor")}</Table.Th>
                  <Table.Th>{t("common.type","Type")}</Table.Th>
                  <Table.Th>{t("license.period","Period")}</Table.Th>
                  <Table.Th>{t("license.payments","Payments")}</Table.Th>
                  <Table.Th>{t("license.totalValue","Total Value")}</Table.Th>
                  <Table.Th>{t("common.status","Status")}</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {contracts.map((c: any) => {
                  const total = (c.payments ?? []).reduce((s: number, p: any) => s + Number(p.amount || 0), 0)
                  const statusColor: Record<string,string> = { ACTIVE:'teal', EXPIRING:'orange', EXPIRED:'red', CANCELLED:'gray' }
                  const fmtD = (d: any) => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—'
                  return (
                    <Table.Tr key={c.id}>
                      <Table.Td>
                        <Text size="sm" fw={600}>{c.title}</Text>
                        {c.contractNumber && <Text size="xs" c="dimmed">#{c.contractNumber}</Text>}
                      </Table.Td>
                      <Table.Td><Text size="sm">{c.vendor ?? '—'}</Text></Table.Td>
                      <Table.Td>
                        <Badge size="xs" variant="light" color="blue">
                          {CONTRACT_TYPES_OPT.find(t => t.value === c.type)?.label?.replace(/^[^\s]+ /,'') ?? c.type}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">{fmtD(c.startDate)}</Text>
                        <Text size="xs" c="dimmed">{c.endDate ? `→ ${fmtD(c.endDate)}` : 'Open-ended'}</Text>
                      </Table.Td>
                      <Table.Td><Text size="sm">{(c.payments ?? []).length} งวด</Text></Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={600}>{total > 0 ? fmtMoney(total, c.currency) : '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={statusColor[c.status] ?? 'gray'} variant="light" size="sm">{c.status}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <ActionIcon size="sm" variant="subtle" onClick={() => handleOpenEditContract(c)}>
                            <IconEdit size={14} />
                          </ActionIcon>
                          <ActionIcon size="sm" variant="subtle" color="red" onClick={() => handleDeleteContract(c)}>
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Box>
      )}

      {/* ── Contract Create/Edit Modal ── */}
      <Modal opened={contractModalOpen} onClose={closeContractModal} size="xl" radius="lg"
        scrollAreaComponent={ScrollArea.Autosize}
        title={
          <Group gap={8}>
            <IconCoin size={16} color="#6366f1" />
            <Text fw={600}>{editingContract ? 'Edit Contract' : 'New Contract'}</Text>
          </Group>
        }>
        <Stack gap="sm">
          <TextInput label="Contract Title *" placeholder="e.g. SMT Maintenance Agreement 2024-2027"
            value={contractForm.title} onChange={e => setContractForm((p: any) => ({ ...p, title: e.target.value }))} />
          <Group grow gap={8}>
            <TextInput label={t("common.vendor","Vendor")} value={contractForm.vendor}
              onChange={e => setContractForm((p: any) => ({ ...p, vendor: e.target.value }))} />
            <TextInput label={t("license.contractNumber","Contract Number")} value={contractForm.contractNumber}
              onChange={e => setContractForm((p: any) => ({ ...p, contractNumber: e.target.value }))} />
          </Group>
          <Group grow gap={8}>
            <Select label={t("license.contractType","Contract Type")} data={CONTRACT_TYPES_OPT}
              value={contractForm.type} allowDeselect={false}
              onChange={(v: any) => setContractForm((p: any) => ({ ...p, type: v ?? 'SERVICE' }))} />
            <Select label={t("license.currency","Currency")}
              data={['THB','USD','EUR','JPY','SGD','GBP'].map(c => ({ value: c, label: c }))}
              value={contractForm.currency} allowDeselect={false}
              onChange={(v: any) => setContractForm((p: any) => ({ ...p, currency: v ?? 'THB' }))} />
          </Group>
          <Group grow gap={8}>
            <SmartDateInput label="Start Date *" value={contractForm.startDate}
              onChange={(v: string) => setContractForm((p: any) => ({ ...p, startDate: v }))} />
            <SmartDateInput label={t("license.endDate","End Date")} value={contractForm.endDate}
              onChange={(v: string) => setContractForm((p: any) => ({ ...p, endDate: v }))} clearable />
          </Group>
          <Group grow gap={8}>
            <TextInput label={t("license.contactName","Contact Name")} value={contractForm.contactName}
              onChange={e => setContractForm((p: any) => ({ ...p, contactName: e.target.value }))} />
            <TextInput label={t("license.contactEmail","Contact Email")} value={contractForm.contactEmail}
              onChange={e => setContractForm((p: any) => ({ ...p, contactEmail: e.target.value }))} />
          </Group>
          <Textarea label={t("license.descriptionNotes","Description / Notes")} minRows={2} value={contractForm.notes}
            onChange={e => setContractForm((p: any) => ({ ...p, notes: e.target.value }))} />

          <Divider label={
            <Group gap={6}>
              <IconCoin size={13} />
              <Text size="xs" fw={600}>{t("license.paymentSchedule","Payment Schedule")}</Text>
              {contractPayments.length > 0 && (
                <Badge size="xs" color="violet">Total: {fmtMoney(contractTotal, contractForm.currency)}</Badge>
              )}
            </Group>
          } labelPosition="left" />

          {contractPayments.map((pay: any, idx: number) => (
            <Paper key={pay._key ?? idx} withBorder radius="md" p="sm"
              style={{ border: '1px solid #e2e8f0' }}>
              <Group justify="space-between" mb={6}>
                <Text size="xs" fw={600} c="dimmed">Payment {idx + 1}</Text>
                <ActionIcon size="xs" color="red" variant="subtle"
                  onClick={() => setContractPayments(p => p.filter((_: any, i: number) => i !== idx))}>
                  <IconX size={12} />
                </ActionIcon>
              </Group>
              <Group grow gap={8} wrap="wrap">
                <TextInput size="xs" label={t("common.description","Description")} placeholder="e.g. Year 1, Q3 2026"
                  value={pay.description ?? ''}
                  onChange={e => setContractPayments(p => p.map((r: any, i: number) => i === idx ? { ...r, description: e.target.value } : r))} />
                <SmartDateInput label={t("license.dueDate","Due Date")} value={pay.dueDate ?? ''}
                  onChange={(v: string) => setContractPayments(p => p.map((r: any, i: number) => i === idx ? { ...r, dueDate: v } : r))} />
                <NumberInput size="xs" label={t("common.amount","Amount")} min={0} thousandSeparator=","
                  value={pay.amount ?? 0}
                  onChange={(v: any) => setContractPayments(p => p.map((r: any, i: number) => i === idx ? { ...r, amount: Number(v) } : r))} />
                <Select size="xs" label={t("common.frequency","Frequency")} data={PAYMENT_FREQ} allowDeselect={false}
                  value={pay.frequency ?? 'YEARLY'}
                  onChange={(v: any) => setContractPayments(p => p.map((r: any, i: number) => i === idx ? { ...r, frequency: v } : r))} />
              </Group>
            </Paper>
          ))}

          <Button variant="subtle" size="xs" leftSection={<IconPlus size={12} />}
            onClick={() => setContractPayments(p => [...p, emptyPaymentRow()])}>
            + Add Payment
          </Button>

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={closeContractModal}>{t("common.cancel","Cancel")}</Button>
            <Button loading={savingContract} onClick={handleSaveContract}
              style={{ background: '#6366f1', color: '#fff' }}>
              {editingContract ? 'Save Changes' : 'Create Contract'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <HistoryTimelineModal
        opened={historyOpened}
        onClose={closeHistory}
        title={`${t('license.history', 'License History')} ${historyTarget ? `— ${historyTarget.productName}` : ''}`}
        data={historyData}
        loading={loadingHistory}
        typeConfig={{
          LICENSE_CREATED:     { color: 'blue',   label: t('license.hist.created', 'Created'),         icon: '📝' },
          LICENSE_UPDATED:     { color: 'gray',   label: t('license.hist.updated', 'Updated'),          icon: '✏️' },
          LICENSE_ASSIGNED:    { color: 'teal',   label: t('license.hist.assigned', 'Assigned'),        icon: '👤' },
          LICENSE_UNASSIGNED:  { color: 'orange', label: t('license.hist.unassigned', 'Unassigned'),    icon: '↩️' },
          LICENSE_RENEWED:     { color: 'teal',   label: t('license.hist.renewed', 'Renewed'),          icon: '🔄' },
          LICENSE_DEACTIVATED: { color: 'red',    label: t('license.hist.deactivated', 'Deactivated'),  icon: '🚫' },
          LICENSE_REACTIVATED: { color: 'teal',   label: t('license.hist.reactivated', 'Reactivated'),  icon: '✅' },
          LICENSE_EXPIRED:     { color: 'red',    label: t('license.hist.expired', 'Expired'),          icon: '⏰' },
        }}
      />

    </Box>
  )
}
